import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { getRoles } from "../util/getRoles";
import { getResource as rbacGetResource } from "../util/getResourcePermission";
import config from "../permission/config.json";
import { AiOutlineDashboard } from "react-icons/ai";
import { FaChartLine } from "react-icons/fa";
import ExpenseIcon from "../containers/Sidebar/sidebarIcon/ExpenseIcon";
import { ImTree } from "react-icons/im";
import { BsClipboard2CheckFill } from "react-icons/bs";
import { FundOutlined } from "@ant-design/icons";
import TeamInfoIcon from "../containers/Sidebar/sidebarIcon/TeamInfoIcon";
import OnboardingIcon from "../containers/Sidebar/sidebarIcon/OnboardingIcon";
import AuditIcon from "../containers/Sidebar/sidebarIcon/AuditIcon";
import PermissionmanagerIcon from "../containers/Sidebar/sidebarIcon/PermissionmanagerIcon";

// This hook returns the menu array for the current user's role
// It replicates the menu structure from SidebarContent.js
// Menu arrays are large, so we build them dynamically based on role

export const useRoleMenu = () => {
  const companySlug = localStorage.getItem("companyDomain");
  const { authUser } = useSelector(({ auth }) => auth);

  // ── Plan-aware getResource ────────────────────────────────────────────────
  // Mirrors the local getResource defined in SidebarContent.js.
  // Priority 1: SuperAdmin plan check (sidebarModuleAccess from login).
  // Priority 2: Internal RBAC check (resourceAccess from login).
  const sidebarModuleAccess = (() => {
    try {
      const raw = JSON.parse(localStorage.getItem("sidebarModuleAccess") || "[]");
      return new Set(Array.isArray(raw) ? raw : []);
    } catch { return new Set(); }
  })();

  // Plan gate for items that bypass getResource (permission: []).
  // Returns true when no plan is configured (sidebarModuleAccess empty) or slug is present.
  const isPlanEnabled = (slug) =>
    !slug || sidebarModuleAccess.size === 0 || sidebarModuleAccess.has(slug);

  const normalizeKey = (value = "") =>
    String(value || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

  // Skills / Career Path exist in the plan catalogue but aren't yet enabled on
  // any plan, so gating on them would hide the whole module. Mirrors the
  // PLAN_EXEMPT_RESOURCE_KEYS exemption in SidebarContent.js.
  const PLAN_EXEMPT_RESOURCE_KEYS = new Set(["skills", "career_path"]);

  // Maps RBAC resource display names (normalized) to plan-catalog slugs.
  // Keys MUST match the normalized form of config.RESOURCES values.
  // (Same mapping as RESOURCE_TO_SIDEBAR_SLUGS in SidebarContent.js)
  const RESOURCE_TO_SIDEBAR_SLUGS = {
    employee_in_out: ["my_in_out", "employee_in_out"],
    leave: ["leave_application", "user_leave_management", "admin_leave_management"],
    attendance_regularization: ["attendance_regularization", "admin_regularization", "manage_team_regularization"],
    separation: ["resignation"],
    separation_management: ["resignation_employee_list", "last_working_day_list"],
    ticket: ["tickets"],
    employee_ticket: ["employee_tickets"],
    my_assets: ["user_assets"],
    attendance_report: ["user_reports", "admin_reports"],
    employee_report: ["admin_reports"],
    ranking: ["individual_rank"],
    my_goals: ["goals", "my_goals"],
    team_goals: ["goals", "team_goals"],
    bill_reimbursement: ["bill_reimbursement"],
    bill_reimbursement_approval: ["admin_bill_reimbursement"],
    employee_master: ["employee_master"],
    admin_leave_management: ["admin_leave_management"],
    leave_management: ["user_leave_management"],
    pending_task: ["pending_task"],
    admin_regularization: ["admin_regularization"],
    team_regularization: ["manage_team_regularization"],
    last_working_day_list: ["last_working_day_list"],
    onboarding_initiation: ["doc_master", "onboarding_master", "onboarding_initiation"],
    candidate_approval: ["candidate_approval", "candidate_list"],
    onboarding_documents_approval_list: ["doc_approve_list"],
    new_joinees: ["new_joinees_list"],
    job_opening: ["job_opening"],
    inventory: ["inventory"],
    employee_details: ["employee_details"],
    holidays: ["mood_analytics"],
    survey: ["360_survey", "questionnaire", "360_reviews"],
    "360_degree_questionnaire": ["questionnaire"],
    "360_degree_review": ["360_reviews"],
    lms_course_management: ["lms_courses", "lms_cert_template"],
    lms_assessment_management: ["lms_assessments", "lms_grading"],
    lms_learning_path: ["lms_learning_paths"],
    lms_enrollment: ["lms_enrollments"],
    lms_auto_assignment: ["lms_auto_assignment"],
    lms_my_learning: ["lms_my_learning"],
    lms_team_learning: ["lms_team_learning"],
    lms_reports: ["lms_reports", "lms_data_retention"],
    lms_certificates: ["lms_certificates"],
    performance_dashboard: ["performance_dashboard"],
    performance_goals: ["performance_goals"],
    performance_kpi_kra: ["performance_kpi_kra"],
    performance_appraisal: ["performance_appraisal"],
    performance_reports: ["performance_reports"],
    performance_cycles: ["performance_cycles"],
    performance_templates: ["performance_templates"],
    performance_development_plan: ["performance_development_plan"],
    performance_settings: ["performance_settings"],
    expense_dashboard: ["expense_dashboard"],
    expense_advance: ["expense_advance"],
    expense_submission: ["expense_submission"],
    expense_approval: ["expense_approval"],
    expense_reports: ["expense_reports"],
    expense_configuration: ["expense_configuration"],
    // Skills and Career Growth Path each have one RBAC resource covering every
    // screen in the module, so each maps to a single plan slug.
    skills: ["skill_master"],
    career_path: ["career_path"],
    // Shift Swap / Double Shift / Shift Change (docs/swap-shift) — the 3
    // plan-catalog slugs are defined in docs/swap-shift/08-permissions-testing-
    // rollout.md (HRMS_SIDEBAR_CATALOG entries, Phase 8, not built yet
    // server-side). Both RBAC resources map to all 3 slugs since neither
    // resource is split per request-type — Phase 8 owns any finer split.
    shift_swap: ["shift_swap_request", "shift_change_request", "double_shift_request"],
    shift_change: ["shift_swap_request", "shift_change_request", "double_shift_request"],
  };

  const getResource = (name, types = []) => {
    const key = normalizeKey(name);
    if (sidebarModuleAccess.size > 0 && !PLAN_EXEMPT_RESOURCE_KEYS.has(key)) {
      const mappedKeys = RESOURCE_TO_SIDEBAR_SLUGS[key] || [key];
      const hasPlanAccess = mappedKeys.some((mappedKey) => sidebarModuleAccess.has(mappedKey));
      if (!hasPlanAccess) return false;
    }
    return rbacGetResource(name, types);
  };
  // ─────────────────────────────────────────────────────────────────────────

  const PERFORMANCE_COMPANY_ID = '696a1961b51829eda4951b33';

  const isAdmin = getRoles(["Admin"]);
  const isHR = getRoles(["HR"]);

  // Mirrors SidebarContent.js - each item gated by plan+RBAC via getResource.
  const performanceMenu = {
    icon: <FaChartLine />,
    className: "main-submenu",
    key: "sub_performance",
    title: "Performance",
    submenu: true,
    subMenuInfo: [
      ...(getResource(config.RESOURCES.PERFORMANCE_DASHBOARD, ["read"]) ? [{
        key: "performance-dashboard",
        path: `/${companySlug}/performance/dashboard`,
        title: "sidebar.performance.dashboard",
        permission: [config.RESOURCES.PERFORMANCE_DASHBOARD],
      }] : []),
      ...(getResource(config.RESOURCES.PERFORMANCE_GOALS, ["read"]) ? [{
        key: "performance-goals",
        path: `/${companySlug}/performance/goals`,
        title: "sidebar.performance.goals",
        permission: [config.RESOURCES.PERFORMANCE_GOALS],
      }] : []),
      ...(getResource(config.RESOURCES.PERFORMANCE_KPI_KRA, ["read"]) ? [{
        key: "performance-kpi-kra",
        path: `/${companySlug}/performance/kpi-kra`,
        title: "sidebar.performance.kpi_kra",
        permission: [config.RESOURCES.PERFORMANCE_KPI_KRA],
      }] : []),
      ...(getResource(config.RESOURCES.PERFORMANCE_APPRAISAL, ["read"]) ? [{
        key: "performance-appraisals",
        path: `/${companySlug}/performance/appraisal`,
        title: "sidebar.performance.appraisals",
        permission: [config.RESOURCES.PERFORMANCE_APPRAISAL],
      }] : []),
      ...(getResource(config.RESOURCES.PERFORMANCE_REPORTS, ["read"]) ? [{
        key: "performance-reports",
        path: `/${companySlug}/performance/reports`,
        title: "sidebar.performance.reports",
        permission: [config.RESOURCES.PERFORMANCE_REPORTS],
      }] : []),
      ...(getResource(config.RESOURCES.PERFORMANCE_CYCLES, ["read"]) ? [{
        key: "performance-cycles",
        path: `/${companySlug}/performance/cycles`,
        title: "sidebar.performance.cycles",
        permission: [config.RESOURCES.PERFORMANCE_CYCLES],
      }] : []),
      ...(getResource(config.RESOURCES.PERFORMANCE_TEMPLATES, ["read"]) ? [{
        key: "performance-templates",
        path: `/${companySlug}/performance/templates`,
        title: "sidebar.performance.templates",
        permission: [config.RESOURCES.PERFORMANCE_TEMPLATES],
      }] : []),
    ],
  };

  const hasAnyLmsPermission = () =>
    getResource(config.RESOURCES.LMS_COURSE_MANAGEMENT, ["read"]) ||
    getResource(config.RESOURCES.LMS_ASSESSMENT_MANAGEMENT, ["read"]) ||
    getResource(config.RESOURCES.LMS_ENROLLMENT, ["read"]) ||
    getResource(config.RESOURCES.LMS_MY_LEARNING, ["read"]) ||
    getResource(config.RESOURCES.LMS_CERTIFICATES, ["read"]) ||
    getResource(config.RESOURCES.LMS_LEARNING_PATH, ["read"]) ||
    getResource(config.RESOURCES.LMS_AUTO_ASSIGNMENT, ["read"]) ||
    getResource(config.RESOURCES.LMS_TEAM_LEARNING, ["read"]) ||
    getResource(config.RESOURCES.LMS_REPORTS, ["read"]) ||
    getResource(config.RESOURCES.LMS_COURSE_MANAGEMENT, ["update"]);

  const learningMenu = {
    icon: <i className="fas fa-graduation-cap" />,
    className: "main-submenu",
    key: "lms_module_sidebar",
    title: "Learning",
    submenu: true,
    subMenuInfo: [
      ...(getResource(config.RESOURCES.LMS_COURSE_MANAGEMENT, ["read"]) ? [{
        key: "lms_courses_sidebar",
        path: `/${companySlug}/lms/courses`,
        title: "Course Management",
        submenu: false,
        permission: [config.RESOURCES.LMS_COURSE_MANAGEMENT],
      }] : []),
      ...(getResource(config.RESOURCES.LMS_ASSESSMENT_MANAGEMENT, ["read"]) ? [{
        key: "lms_assessments_sidebar",
        path: `/${companySlug}/lms/assessments`,
        title: "Assessments",
        submenu: false,
        permission: [config.RESOURCES.LMS_ASSESSMENT_MANAGEMENT],
      }] : []),
      ...(getResource(config.RESOURCES.LMS_ENROLLMENT, ["read"]) ? [{
        key: "lms_enrollments_sidebar",
        path: `/${companySlug}/lms/enrollments`,
        title: "Enrollments",
        submenu: false,
        permission: [config.RESOURCES.LMS_ENROLLMENT],
      }] : []),
      ...(getResource(config.RESOURCES.LMS_MY_LEARNING, ["read"]) ? [{
        key: "lms_my_learning_sidebar",
        path: `/${companySlug}/lms/my-learning`,
        title: "My Learning",
        submenu: false,
        permission: [config.RESOURCES.LMS_MY_LEARNING],
      }] : []),
      ...(getResource(config.RESOURCES.LMS_CERTIFICATES, ["read"]) ? [{
        key: "lms_certificates_sidebar",
        path: `/${companySlug}/lms/certificates`,
        title: "Certificates",
        submenu: false,
        permission: [config.RESOURCES.LMS_CERTIFICATES],
      }] : []),
      ...(getResource(config.RESOURCES.LMS_LEARNING_PATH, ["read"]) ? [{
        key: "lms_learning_paths_sidebar",
        path: `/${companySlug}/lms/learning-paths`,
        title: "Learning Paths",
        submenu: false,
        permission: [config.RESOURCES.LMS_LEARNING_PATH],
      }] : []),
      ...(getResource(config.RESOURCES.LMS_AUTO_ASSIGNMENT, ["read"]) ? [{
        key: "lms_auto_assignment_sidebar",
        path: `/${companySlug}/lms/auto-assignment`,
        title: "Auto-Assignment",
        submenu: false,
        permission: [config.RESOURCES.LMS_AUTO_ASSIGNMENT],
      }] : []),
      ...(getResource(config.RESOURCES.LMS_TEAM_LEARNING, ["read"]) ? [{
        key: "lms_team_learning_sidebar",
        path: `/${companySlug}/lms/team-learning`,
        title: "Team Learning",
        submenu: false,
        permission: [config.RESOURCES.LMS_TEAM_LEARNING],
      }] : []),
      ...(getResource(config.RESOURCES.LMS_REPORTS, ["read"]) ? [{
        key: "lms_reports_sidebar",
        path: `/${companySlug}/lms/reports`,
        title: "LMS Reports",
        submenu: false,
        permission: [config.RESOURCES.LMS_REPORTS],
      }] : []),
      ...(getResource(config.RESOURCES.LMS_ASSESSMENT_MANAGEMENT, ["read"]) ? [{
        key: "lms_grading_sidebar",
        path: `/${companySlug}/lms/grading`,
        title: "Manual Grading",
        submenu: false,
        permission: [config.RESOURCES.LMS_ASSESSMENT_MANAGEMENT],
      }] : []),
      ...(getResource(config.RESOURCES.LMS_REPORTS, ["read"]) ? [{
        key: "lms_data_retention_sidebar",
        path: `/${companySlug}/lms/settings/data-retention`,
        title: "Data Retention",
        submenu: false,
        permission: [config.RESOURCES.LMS_REPORTS],
      }] : []),
      ...(getResource(config.RESOURCES.LMS_COURSE_MANAGEMENT, ["update"]) ? [{
        key: "lms_cert_template_sidebar",
        path: `/${companySlug}/lms/settings/certificate-template`,
        title: "Certificate Template",
        submenu: false,
        permission: [config.RESOURCES.LMS_COURSE_MANAGEMENT],
      }] : []),
    ],
  };

  // Enhanced Expense Management submenu - each item gated by RBAC via getResource,
  // matching the same pattern as every other sidebar module.
  const expenseMenu = {
    icon: <ExpenseIcon />,
    className: "main-submenu",
    key: "sub_expense",
    title: "Expense Management",
    submenu: true,
    subMenuInfo: [
      ...(rbacGetResource(config.RESOURCES.EXPENSE_DASHBOARD, ["read"]) ? [{
        key: "expense-dashboard",
        path: `/${companySlug}/expense/dashboard`,
        title: "Expense Dashboard",
        submenu: false,
        permission: [config.RESOURCES.EXPENSE_DASHBOARD],
      }] : []),
      ...(rbacGetResource(config.RESOURCES.EXPENSE_ADVANCE, ["read"]) ? [{
        key: "expense-advances",
        path: `/${companySlug}/expense/advances`,
        title: "Advance Requests",
        submenu: false,
        permission: [config.RESOURCES.EXPENSE_ADVANCE],
      }] : []),
      ...(rbacGetResource(config.RESOURCES.EXPENSE_SUBMISSION, ["read"]) ? [{
        key: "expense-submit",
        path: `/${companySlug}/expense/submit`,
        title: "Submit Expense",
        submenu: false,
        permission: [config.RESOURCES.EXPENSE_SUBMISSION],
      }] : []),
      ...(rbacGetResource(config.RESOURCES.EXPENSE_APPROVAL, ["read"]) ? [{
        key: "expense-approvals",
        path: `/${companySlug}/expense/approvals`,
        title: "Approval Queue",
        submenu: false,
        permission: [config.RESOURCES.EXPENSE_APPROVAL],
      }] : []),
      ...(rbacGetResource(config.RESOURCES.EXPENSE_REPORTS, ["read"]) ? [{
        key: "expense-reports",
        path: `/${companySlug}/expense/reports`,
        title: "Expense Reports",
        submenu: false,
        permission: [config.RESOURCES.EXPENSE_REPORTS],
      }] : []),
      ...(rbacGetResource(config.RESOURCES.EXPENSE_CONFIG, ["read"]) ? [{
        key: "expense-config",
        path: `/${companySlug}/expense/config`,
        title: "Expense Configuration",
        submenu: false,
        permission: [config.RESOURCES.EXPENSE_CONFIG],
      }] : []),
    ],
  };
  
  // Skills submenu - mirrors the "Skills sidebar" block in SidebarContent.js.
  // Skills Matrix and Skill Inventory hold their own dedicated resources;
  // the rest still ride the single SKILL resource.
  const skillsMenu = {
    icon: <i className="fas fa-brain" />,
    className: "main-submenu",
    key: "skills_module_sidebar",
    title: "Skills",
    submenu: true,
    subMenuInfo: [
      ...(getResource(config.RESOURCES.SKILL, ["read"]) ? [{
        key: "skills_master_sidebar",
        path: `/${companySlug}/skills`,
        title: "Skill Master",
        submenu: false,
        permission: [config.RESOURCES.SKILL],
      }] : []),
      ...(getResource(config.RESOURCES.SKILLS_MATRIX, ["read"]) ? [{
        key: "skills_matrix_sidebar",
        path: `/${companySlug}/skills-matrix`,
        title: "Skills Matrix",
        submenu: false,
        permission: [config.RESOURCES.SKILLS_MATRIX],
      }] : []),
      ...(getResource(config.RESOURCES.SKILL_INVENTORY, ["read"]) ? [{
        key: "skill_inventory_sidebar",
        path: `/${companySlug}/skill-inventory`,
        title: "Organization Skill Inventory",
        submenu: false,
        permission: [config.RESOURCES.SKILL_INVENTORY],
      }] : []),
      ...(getResource(config.RESOURCES.SKILL, ["read"]) ? [{
        key: "role_skill_requirements_sidebar",
        path: `/${companySlug}/role-skill-requirements`,
        title: "Role Skill Requirements",
        submenu: false,
        permission: [config.RESOURCES.SKILL],
      }] : []),
      ...(getResource(config.RESOURCES.SKILL, ["read"]) ? [{
        key: "skill_gap_analysis_sidebar",
        path: `/${companySlug}/skill-gap-analysis`,
        title: "Skill Gap Analysis",
        submenu: false,
        permission: [config.RESOURCES.SKILL],
      }] : []),
    ],
  };

  // Career Growth Path submenu - mirrors the "Career Growth Path sidebar" block
  // in SidebarContent.js. Its OWN resource, not Skills': a user holding Career
  // Path but notoarding
//  Skills must still see this group.
  const careerPathMenu = {
    icon: <i className="fas fa-route" />,
    className: "main-submenu",
    key: "career_path_module_sidebar",
    title: "Career Growth Path",
    submenu: true,
    subMenuInfo: [
      ...(getResource(config.RESOURCES.CAREER_PATH, ["read"]) ? [{
        key: "career_paths_sidebar",
        path: `/${companySlug}/career-paths`,
        title: "Career Growth Path",
        submenu: false,
        permission: [config.RESOURCES.CAREER_PATH],
      }] : []),
      ...(getResource(config.RESOURCES.EMPLOYEE_CAREER_PATH, ["read"]) ? [{
        key: "employee_career_paths_sidebar",
        path: `/${companySlug}/employee-career-paths`,
        title: "Employee Career Paths",
        submenu: false,
        permission: [config.RESOURCES.EMPLOYEE_CAREER_PATH],
      }] : []),
      ...(getResource(config.RESOURCES.EMPLOYEE_CAREER_VIEW, ["read"]) ? [{
        key: "employee_career_view_sidebar",
        path: `/${companySlug}/employee-career-view`,
        title: "Employee Career View",
        submenu: false,
        permission: [config.RESOURCES.EMPLOYEE_CAREER_VIEW],
      }] : []),
      ...(getResource(config.RESOURCES.CAREER_READINESS, ["read"]) ? [{
        key: "career_readiness_sidebar",
        path: `/${companySlug}/career-readiness`,
        title: "Career Readiness",
        submenu: false,
        permission: [config.RESOURCES.CAREER_READINESS],
      }] : []),
      ...(getResource(config.RESOURCES.EMPLOYEE_COMPETENCY, ["read"]) ? [{
        key: "employee_competencies_sidebar",
        path: `/${companySlug}/employee-competencies`,
        title: "Employee Competencies",
        submenu: false,
        permission: [config.RESOURCES.EMPLOYEE_COMPETENCY],
      }] : []),
      ...(getResource(config.RESOURCES.COURSE_SKILL_MAPPING, ["read"]) ? [{
        key: "course_skill_mapping_sidebar",
        path: `/${companySlug}/course-skill-mapping`,
        title: "Course Skill Mapping",
        submenu: false,
        permission: [config.RESOURCES.COURSE_SKILL_MAPPING],
      }] : []),
    ],
  };



  // Build User menu array - same structure as SidebarContent
  // "Engage" submenu — company communications (Announcements). Shown to
  // Admin/HR when the announcement plan module is enabled and RBAC grants read.
  const buildEngageMenu = () => {
    // Each Engage cluster is gated by its own plan-catalog slug: Announcements
    // by "announcement", the Wall/Awards/Badges/Moderation/Settings cluster
    // by "wall". Overview is shown whenever any one of those modules is
    // granted. Celebrations is NOT plan-gated or permission-gated — it has
    // no RBAC resource and always shows, by design (2026-09-15) — so the
    // "Engage" parent itself must never collapse to null even when
    // announcement/wall are both off; Celebrations alone still needs it.
    return {
      icon: <i className="fas fa-bullhorn" />,
      className: "main-submenu",
      key: "engage",
      title: "Engage",
      submenu: true,
      subMenuInfo: [
        // Overview is the Wall/Awards/Badges dashboard — it belongs to the Wall &
        // Recognition cluster, not to Announcements or Celebrations alone.
        ...(isPlanEnabled("wall") ? [{
          key: "engage-overview",
          path: `/${companySlug}/engage/overview`,
          title: "sidebar.engageOverview",
          submenu: false,
          permission: [config.RESOURCES.ENGAGE_PANEL],
        }] : []),
        ...(isPlanEnabled("announcement") ? [{
          key: "engage-announcements",
          path: `/${companySlug}/engage/announcements`,
          title: "sidebar.announcement",
          submenu: false,
          permission: [],
        }] : []),
        {
          key: "engage-celebrations",
          path: `/${companySlug}/engage/celebrations`,
          title: "sidebar.celebrations",
          submenu: false,
          permission: [],
        },
        ...(isPlanEnabled("wall") ? [{
          key: "engage-awards",
          path: `/${companySlug}/engage/awards`,
          title: "sidebar.awards",
          submenu: false,
          permission: [config.RESOURCES.ENGAGE_PANEL],
        }] : []),
        ...(isPlanEnabled("wall") ? [{
          key: "engage-badges",
          path: `/${companySlug}/engage/badges`,
          title: "sidebar.badges",
          submenu: false,
          permission: [config.RESOURCES.ENGAGE_PANEL],
        }] : []),
        ...(isPlanEnabled("wall") ? [{
          key: "engage-wall",
          path: `/${companySlug}/engage/wall`,
          title: "sidebar.engageWall",
          submenu: false,
          permission: [config.RESOURCES.ENGAGE_WALL],
        }] : []),
        ...(isPlanEnabled("wall") ? [{
          key: "engage-moderation",
          path: `/${companySlug}/engage/moderation`,
          title: "sidebar.engageModeration",
          submenu: false,
          permission: [config.RESOURCES.ENGAGE_PANEL],
        }] : []),
        ...(isPlanEnabled("wall") ? [{
          key: "engage-settings",
          path: `/${companySlug}/engage/settings`,
          title: "sidebar.engageSettings",
          submenu: false,
          permission: [config.RESOURCES.ENGAGE_PANEL],
        }] : []),
      ],
    };
  };

  const buildUserMenu = () => {
    return [
      {
        key: "User Dashboard",
        path: `/${companySlug}/dashboard`,
        icon: <AiOutlineDashboard />,
        title: "sidebar.User_Dashboard",
        submenu: false,
        permission: [],
      },
      // "Me" submenu
      ...(getResource(config.RESOURCES.LEAVE, ["read"]) ||
        getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"]) ||
        getResource(config.RESOURCES.SEPARATION, ["read"]) ||
        getResource(config.RESOURCES.TICKET, ["read"]) ||
        getResource(config.RESOURCES.MY_ASSETS, ["read"]) ||
        getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"]) ||
        getResource(config.RESOURCES.RANKING, ["read"]) ||
        getResource(config.RESOURCES.MY_GOAL, ["read"]) ||
        // getResource(config.RESOURCES.AUDITS, ["read"]) ||
        getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"])
        ? [
          {
            key: "Me",
            title: "Me",
            className: " main-submenu",
            icon: <i className="fas fa-user " />,
            submenu: true,
            subMenuInfo: [
              ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])) ? [{
                key: "My-In-Out",
                path: `/${companySlug}/my-in-out`,
                title: "sidebar.My-In-Out",
                submenu: false,
                permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
              }] : [],
              ...(getResource(config.RESOURCES.LEAVE, ["read"])) ? [{
                key: "manage-leave-application",
                path: `/${companySlug}/leave-application`,
                title: "sidebar.leave-application",
                permission: [config.RESOURCES.LEAVE],
              }] : [],
              ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])) ? [{
                key: "attendance-regularization",
                path: `/${companySlug}/employee-attendence-regularization`,
                title: "sidebar.Attendence_Regularization",
                permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
              }] : [],
              ...(getResource(config.RESOURCES.SEPARATION, ["read"])) ? [{
                key: "resignation",
                path: `/${companySlug}/resignation`,
                title: "sidebar.resignation",
                permission: [config.RESOURCES.SEPARATION],
              }] : [],
              ...(getResource(config.RESOURCES.TICKET, ["read"])) ? [{
                key: "tickets",
                path: `/${companySlug}/tickets`,
                title: "sidebar.tickets",
                permission: [config.RESOURCES.TICKET],
              }] : [],
              ...(getResource(config.RESOURCES.MY_ASSETS, ["read"])) ? [{
                key: "assets",
                path: `/${companySlug}/user-assets`,
                title: "sidebar.user-assets",
                permission: [config.RESOURCES.MY_ASSETS],
              }] : [],
              ...(getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"])) ? [{
                key: "user-reports",
                path: `/${companySlug}/user-reports`,
                title: "sidebar.user-reports",
                permission: [config.RESOURCES.ATTENDANCE_REPORT],
              }] : [],
              ...(getResource(config.RESOURCES.RANKING, ["read"])) ? [{
                key: "individual-ranking",
                path: `/${companySlug}/individual-rank`,
                title: "sidebar.individual-rank",
                permission: [config.RESOURCES.RANKING],
              }] : [],
              // ...(getResource(config.RESOURCES.MY_GOAL, ["read"])) ? [{
              //   key: "goals",
              //   path: `/${companySlug}/my-goals`,
              //   title: "sidebar.create-goals",
              //   permission: [config.RESOURCES.MY_GOAL],
              // }] : [],
              // For HR role: "Audits" (/audits) is in "Me" submenu
              // For other roles: "Audits" (/audit-history) is in "Me" submenu
              // ...(getResource(config.RESOURCES.AUDITS, ["read"])) ? [{
              //   key: "audits",
              //   path: `/${companySlug}/my-audit-history`,
              //   title: "Audits",
              //   permission: [config.RESOURCES.AUDITS],
              // }] : [],
              // Project Overview - always shown for User role (when Me submenu exists)
              // ...(getRoles(["User"])) ? [{
              //   key: "proejct-overview",
              //   path: `/${companySlug}/project-overview`,
              //   title: "sidebar.project-overview",
              //   permission: [],
              // }] : [],

              ...(getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"])) ? [
                {
                  key: "Bill Reimbursement",
                  path: `/${companySlug}/bill-reimbursement`,
                  title: "sidebar.bill-reimbursement",
                  submenu: false,
                  permission: [config.RESOURCES.BILLREIMBURSEMENT],
                }] : [],
            ],
          },
        ]
        : []),
      // "Team" submenu
      ...(getResource(config.RESOURCES.EMPLOYEE_MASTER, ["read"]) ||
        getResource(config.RESOURCES.EMPLOYEE_IN_OUT, ["read"]) ||
        getResource(config.RESOURCES.SEPARATION_MANAGEMENT, ["read"]) ||
        getResource(config.RESOURCES.EMPLOYEE_REPORT, ["read"]) ||
        getResource(config.RESOURCES.FINANCE_REPORT, ["read"]) ||
        getResource(config.RESOURCES.EMPLOYEE_TICKET, ["read"]) ||
        getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"]) ||
        getResource(config.RESOURCES.LEAVE_MANAGEMENT, ["read"]) ||
        getResource(config.RESOURCES.ADMIN_REGULARIZATION, ["read"]) ||
        getResource(config.RESOURCES.TEAM_REGULARIZATION, ["read"]) ||
        getResource(config.RESOURCES.KPI, ["read"]) ||
        getResource(config.RESOURCES.LAST_WORKING_DAY_LIST, ["read"])
        ? [
          {
            icon: <TeamInfoIcon />,
            className: "main-submenu",
            key: "sub16",
            title: "Team",
            submenu: true,
            subMenuInfo: [
              ...(getResource(config.RESOURCES.EMPLOYEE_MASTER, ["read"])) ? [{
                key: "employee-master",
                path: `/${companySlug}/employee-master`,
                title: "sidebar.employee-master",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_MASTER],
              }] : [],
              ...(getResource(config.RESOURCES.EMPLOYEE_IN_OUT, ["read"])) ? [{
                key: "employee-in-out",
                path: `/${companySlug}/employee-in-out`,
                title: "sidebar.Employee-In-Out",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_IN_OUT],
              }] : [],
              ...(getResource(config.RESOURCES.SEPARATION_MANAGEMENT, ["read"])) ? [{
                key: "resignation-employee-list",
                path: `/${companySlug}/resignation-employee-list`,
                title: "sidebar.resignation-employee-list",
                submenu: false,
                permission: [config.RESOURCES.SEPARATION_MANAGEMENT],
              }] : [],
              ...(getResource(config.RESOURCES.LAST_WORKING_DAY_LIST, ["read"])) ? [{
                key: "last-working-day-emps",
                path: `/${companySlug}/last-working-day-emps`,
                title: "Last Working Day List",
                permission: [config.RESOURCES.LAST_WORKING_DAY_LIST],
              }] : [],
              ...(getResource(config.RESOURCES.EMPLOYEE_REPORT, ["read"]) ||
                getResource(config.RESOURCES.LEAVE_REPORT, ["read"]) ||
                getResource(config.RESOURCES.FINANCE_REPORT, ["read"]) ||
                getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"])) ? [{
                key: "admin-reports",
                path: `/${companySlug}/admin-reports`,
                title: "sidebar.admin-reports",
                submenu: false,
                permission: [
                  config.RESOURCES.EMPLOYEE_REPORT,
                  config.RESOURCES.LEAVE_REPORT,
                  config.RESOURCES.ATTENDANCE_REPORT,
                  config.RESOURCES.FINANCE_REPORT,
                ],
              }] : [],
              ...(getResource(config.RESOURCES.EMPLOYEE_TICKET, ["read"])) ? [{
                key: "tickets",
                path: `/${companySlug}/employee-tickets`,
                title: "sidebar.tickets",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_TICKET],
              }] : [],
              ...(getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"])) ? [{
                key: "admin-leave-approval",
                path: `/${companySlug}/admin-leave-approval`,
                title: "sidebar.admin-leave-management",
                submenu: false,
                permission: [config.RESOURCES.ADMIN_LEAVE_MANAGEMENT],
              }] : [],
              ...(getResource(config.RESOURCES.LEAVE_MANAGEMENT, ["read"])) ? [{
                key: "user-leave-approval",
                path: `/${companySlug}/user-leave-approve`,
                title: "sidebar.user-leave-management",
                submenu: false,
                permission: [config.RESOURCES.LEAVE_MANAGEMENT],
              }] : [],
              ...(getResource(config.RESOURCES.ADMIN_REGULARIZATION, ["read"])) ? [{
                key: "admin-regularizatrion",
                path: `/${companySlug}/admin-regularizatrion`,
                title: "sidebar.admin-regularizatrion",
                submenu: false,
                permission: [config.RESOURCES.ADMIN_REGULARIZATION],
              }] : [],
              ...(getResource(config.RESOURCES.TEAM_REGULARIZATION, ["read"])) ? [{
                key: "ManageTeamRegularization",
                path: `/${companySlug}/manage-team-regularization`,
                title: "sidebar.ManageTeamRegularization",
                submenu: false,
                permission: [config.RESOURCES.TEAM_REGULARIZATION],
              }] : [],
              // ...(getResource(config.RESOURCES.KPI, ["read"])) ? [{
              //   key: "Kpis",
              //   path: `/${companySlug}/Kpis`,
              //   title: "sidebar.manage-kpis",
              //   submenu: false,
              //   permission: [config.RESOURCES.KPI],
              // }] : [],
            ],
          },
        ]
        : []),
      // Performance Review submenu
      ...(performanceMenu.subMenuInfo.length > 0 ? [performanceMenu] : []),

      // Learning (LMS) submenu
      ...(hasAnyLmsPermission() ? [learningMenu] : []),

      // Enhanced Expense Management submenu
      ...(expenseMenu.subMenuInfo.length > 0 ? [expenseMenu] : []),
      ...(skillsMenu.subMenuInfo.length > 0 ? [skillsMenu] : []),
      ...(careerPathMenu.subMenuInfo.length > 0 ? [careerPathMenu] : []),

      // Onboarding submenu
      ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"]) ||
        getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"]) ||
        getResource(config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST, ["read"]) ||
        getResource(config.RESOURCES.NEW_JOINEES, ["read"])
        ? [
          {
            icon: <OnboardingIcon />,
            className: "main-submenu",
            key: "sub19",
            title: "Onboarding",
            submenu: true,
            subMenuInfo: [
              ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"])) ? [{
                key: "company-doc-master",
                path: `/${companySlug}/company-doc-master`,
                title: "sidebar.docMaster",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_INITIATION],
              }] : [],
              ...(getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"])) ? [{
                key: "candidate-approvel",
                path: `/${companySlug}/candidate-approval`,
                title: "sidebar.candidate-approvel",
                submenu: false,
                permission: [config.RESOURCES.CANDIDATE_APPROVAL],
              }] : [],
              ...(getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"])) ? [{
                key: "candidate-list",
                path: `/${companySlug}/candidate-list`,
                title: "sidebar.candidate-approvel-list",
                submenu: false,
                permission: [config.RESOURCES.CANDIDATE_APPROVAL],
              }] : [],
              ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"])) ? [{
                key: "initiation",
                path: `/${companySlug}/onboarding-initiation`,
                title: "sidebar.onboarding-initiation",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_INITIATION],
              }] : [],
              ...(getResource(config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST, ["read"])) ? [{
                key: "doc-approve-list",
                path: `/${companySlug}/doc-approve-list`,
                title: "sidebar.doc-approve-list",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST],
              }] : [],
              ...(getResource(config.RESOURCES.NEW_JOINEES, ["read"])) ? [{
                key: "new-joinees-list",
                path: `/${companySlug}/new-joinees-list`,
                title: "sidebar.new-joinees-list",
                submenu: false,
                permission: [config.RESOURCES.NEW_JOINEES],
              }] : [],
            ],
          },
        ]
        : []),
      // Audit submenu
      // ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"]) ||
      //   getResource(config.RESOURCES.AUDITS, ["read"]) ||
      //   getResource(config.RESOURCES.AUDITS_PERFORMANCE, ["read"]) ||
      //   getResource(config.RESOURCES.AUDIT_HEAD, ["read"])
      //   ? [
      //     {
      //       icon: <AuditIcon />,
      //       className: "main-submenu",
      //       key: "sub20",
      //       title: "Audit",
      //       submenu: true,
      //       subMenuInfo: [
      //         // For HR role, Audit History is in Audit submenu, not Me submenu
      //         ...(getRoles(["HR"]) && getResource(config.RESOURCES.AUDITS, ["read"])) ? [{
      //           key: "Audit",
      //           path: `/${companySlug}/audit-history`,
      //           title: "Audit History",
      //           submenu: false,
      //           permission: [config.RESOURCES.AUDITS],
      //         }] : [],
      //         ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"])) ? [{
      //           key: "Audit Master",
      //           path: `/${companySlug}/auditmaster`,
      //           title: "Audits Master",
      //           submenu: false,
      //           permission: [config.RESOURCES.AUDIT_MASTER],
      //         }] : [],
      //         ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"])) ? [{
      //           key: "Audits",
      //           path: `/${companySlug}/audits`,
      //           title: "Audits",
      //           submenu: false,
      //           permission: [config.RESOURCES.AUDIT_MASTER],
      //         }] : [],
      //         ...(getResource(config.RESOURCES.AUDITS_PERFORMANCE, ["read"])) ? [{
      //           key: "auditperformance",
      //           path: `/${companySlug}/auditperformance`,
      //           title: "Audit Performance",
      //           submenu: false,
      //           permission: [config.RESOURCES.AUDITS_PERFORMANCE],
      //         }] : [],
      //         ...(getResource(config.RESOURCES.AUDIT_HEAD, ["read"])) ? [{
      //           key: "auditHead",
      //           path: `/${companySlug}/auditHead`,
      //           title: "Audit Head",
      //           submenu: false,
      //           permission: [config.RESOURCES.AUDIT_HEAD],
      //         }] : [],
      //       ],
      //     },
      //   ]
      //   : []),
      // More submenu - structure depends on role
      // This will be built based on role in the return statement
      // KPI
      ...(getResource(config.RESOURCES["KPI"], ["read"])) ? [{
        key: "Kpis",
        path: `/${companySlug}/user-kpi`,
        icon: <FundOutlined />,
        title: "sidebar.kpis",
        submenu: false,
        permission: [],
      }] : [],
      // Survey submenu
      ...((getResource(config.RESOURCES["SURVEY"], ["read"]) ||
        getResource(config.RESOURCES["360QUE"], ["read"]) ||
        getResource(config.RESOURCES["360REVW"], ["read"]))
        ? [
          {
            icon: <i className="fas fa-poll" />,
            className: "main-submenu",
            key: "sub22",
            title: "Survey",
            submenu: true,
            subMenuInfo: [
              ...(getResource(config.RESOURCES["SURVEY"], ["read"])) ? [{
                key: "360_survey",
                path: `/${companySlug}/360-survey`,
                title: "sidebar.360_survey",
                submenu: false,
                permission: [config.RESOURCES["SURVEY"]],
              }] : [],
              ...(getResource(config.RESOURCES["360QUE"], ["read"])) ? [{
                key: "360_questionnaire",
                path: `/${companySlug}/360-questionnaire`,
                title: "sidebar.360_questionnaire",
                submenu: false,
                permission: [config.RESOURCES["360QUE"]],
              }] : [],
              ...(getResource(config.RESOURCES["360REVW"], ["read"])) ? [{
                key: "360_reviews",
                path: `/${companySlug}/360-reviews`,
                title: "sidebar.360_reviews",
                submenu: false,
                permission: [config.RESOURCES["360REVW"]],
              }] : [],
            ],
          },
        ]
        : []),
      // Organization Tree
      {
        key: "organization-tree",
        path: `/${companySlug}/organization-view`,
        icon: <ImTree />,
        title: "sidebar.organisation-tree",
        submenu: false,
        permission: [],
      },
    ];
  };

  // Build Admin menu array - matches JSX structure from SidebarContent
  const buildAdminMenu = () => {
    return [
      {
        key: "Admin Dashboard",
        path: `/${companySlug}/dashboard`,
        icon: <AiOutlineDashboard />,
        title: "sidebar.Admin_Dashboard",
        submenu: false,
        permission: [],
      },
      // "Me" submenu (sub15)
      {
        key: "sub15",
        title: "Me",
        className: " main-submenu",
        icon: <i className="fas fa-info-circle " />,
        submenu: true,
        subMenuInfo: [
          {
            key: "My-In-Out",
            path: `/${companySlug}/my-in-out`,
            title: "sidebar.My-In-Out",
            permission: [config.RESOURCES.EMPLOYEE_IN_OUT],
          },
          {
            key: "manage-leave-application",
            path: `/${companySlug}/leave-application`,
            title: "sidebar.leave-application",
            permission: [config.RESOURCES.LEAVE],
          },
          ...(getResource(config.RESOURCES.RANKING, ["read"])) ? [{
              key: "individual-ranking",
              path: `/${companySlug}/individual-rank`,
              title: "sidebar.individual-rank",
              permission: [config.RESOURCES.RANKING],
            }] : [],
          ...(getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"])) ? [
            {
              key: "Bill Reimbursement",
              path: `/${companySlug}/bill-reimbursement`,
              title: "sidebar.bill-reimbursement",
              submenu: false,
              permission: [config.RESOURCES.BILLREIMBURSEMENT],
            }] : [],
        ],
      },
      // "Team" submenu (sub16)
      {
        icon: <TeamInfoIcon />,
        className: "main-submenu",
        key: "sub16",
        title: "Team",
        submenu: true,
        subMenuInfo: [
          ...(getResource(config.RESOURCES.EMPLOYEE_MASTER, ["read"])) ? [{
            key: "employee-master",
            path: `/${companySlug}/employee-master`,
            title: "sidebar.employee-master",
            permission: [config.RESOURCES.EMPLOYEE_MASTER],
          }] : [],
          ...(getResource(config.RESOURCES.EMPLOYEE_IN_OUT, ["read"])) ? [{
            key: "employee-in-out",
            path: `/${companySlug}/employee-in-out`,
            title: "sidebar.Employee-In-Out",
            permission: [config.RESOURCES.EMPLOYEE_IN_OUT],
          }] : [],
          ...(getResource(config.RESOURCES.SEPARATION_MANAGEMENT, ["read"])) ? [{
            key: "resignation-employee-list",
            path: `/${companySlug}/resignation-employee-list`,
            title: "sidebar.resignation-employee-list",
            permission: [config.RESOURCES.SEPARATION_MANAGEMENT],
          }] : [],
          ...(getResource(config.RESOURCES.EMPLOYEE_REPORT, ["read"]) ||
            getResource(config.RESOURCES.LEAVE_REPORT, ["read"]) ||
            getResource(config.RESOURCES.FINANCE_REPORT, ["read"]) ||
            getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"])) ? [{
            key: "admin-reports",
            path: `/${companySlug}/admin-reports`,
            title: "sidebar.admin-reports",
            permission: [
              config.RESOURCES.EMPLOYEE_REPORT,
              config.RESOURCES.LEAVE_REPORT,
              config.RESOURCES.ATTENDANCE_REPORT,
              config.RESOURCES.FINANCE_REPORT,
            ],
          }] : [],
          ...(getResource(config.RESOURCES.EMPLOYEE_TICKET, ["read"])) ? [{
            key: "tickets",
            path: `/${companySlug}/employee-tickets`,
            title: "sidebar.tickets",
            permission: [config.RESOURCES.EMPLOYEE_TICKET],
          }] : [],
          ...(getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"])) ? [{
            key: "admin-leave-approval",
            path: `/${companySlug}/admin-leave-approval`,
            title: "sidebar.admin-leave-management",
            permission: [config.RESOURCES.ADMIN_LEAVE_MANAGEMENT],
          }] : [],
          ...(getResource(config.RESOURCES.ADMIN_REGULARIZATION, ["read"])) ? [{
            key: "admin-regularizatrion",
            path: `/${companySlug}/admin-regularizatrion`,
            title: "sidebar.admin-regularizatrion",
            permission: [config.RESOURCES.ADMIN_REGULARIZATION],
          }] : [],
          // ...(getResource(config.RESOURCES.KPI, ["read"])) ? [{
          //   key: "Kpis",
          //   path: `/${companySlug}/Kpis`,
          //   title: "sidebar.manage-kpis",
          //   permission: [config.RESOURCES.KPI],
          // }] : [],
        ],
      },
      // Performance Review submenu
      ...(performanceMenu.subMenuInfo.length > 0 ? [performanceMenu] : []),

      // Learning (LMS) submenu
      ...(hasAnyLmsPermission() ? [learningMenu] : []),

      // Enhanced Expense Management submenu
      ...(expenseMenu.subMenuInfo.length > 0 ? [expenseMenu] : []),
      ...(skillsMenu.subMenuInfo.length > 0 ? [skillsMenu] : []),
      ...(careerPathMenu.subMenuInfo.length > 0 ? [careerPathMenu] : []),

      // "Onboarding" submenu (sub19)
      {
        icon: <OnboardingIcon />,
        className: "main-submenu",
        key: "sub19",
        title: "Onboarding",
        submenu: true,
        subMenuInfo: [
          ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"])) ? [{
            key: "company-doc-master",
            path: `/${companySlug}/company-doc-master`,
            title: "sidebar.docMaster",
            permission: [config.RESOURCES.ON_BOARDING_INITIATION],
          }] : [],
          ...(getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"])) ? [{
            key: "candidate-approvel",
            path: `/${companySlug}/candidate-approval`,
            title: "sidebar.candidate-approvel",
            permission: [config.RESOURCES.CANDIDATE_APPROVAL],
          }] : [],
          ...(getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"])) ? [{
            key: "candidate-list",
            path: `/${companySlug}/candidate-list`,
            title: "sidebar.candidate-approvel-list",
            permission: [config.RESOURCES.CANDIDATE_APPROVAL],
          }] : [],
          ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"])) ? [{
            key: "initiation",
            path: `/${companySlug}/onboarding-initiation`,
            title: "sidebar.onboarding-initiation",
            permission: [config.RESOURCES.ON_BOARDING_INITIATION],
          }] : [],
          ...(getResource(config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST, ["read"])) ? [{
            key: "doc-approve-list",
            path: `/${companySlug}/doc-approve-list`,
            title: "sidebar.doc-approve-list",
            permission: [config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST],
          }] : [],
          ...(getResource(config.RESOURCES.NEW_JOINEES, ["read"])) ? [{
            key: "new-joinees-list",
            path: `/${companySlug}/new-joinees-list`,
            title: "sidebar.new-joinees-list",
            permission: [config.RESOURCES.NEW_JOINEES],
          }] : [],
        ],
      },
      // "Audit" submenu (sub20)
      // {
      //   icon: <AuditIcon />,
      //   className: "main-submenu",
      //   key: "sub20",
      //   title: "Audit",
      //   submenu: true,
      //   subMenuInfo: [
      //     ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"])) ? [{
      //       key: "Audit Master",
      //       path: `/${companySlug}/auditmaster`,
      //       title: "Audits Master",
      //       permission: [config.RESOURCES.AUDIT_MASTER],
      //     }] : [],
      //     ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"])) ? [{
      //       key: "Audits",
      //       path: `/${companySlug}/audits`,
      //       title: "Audits",
      //       permission: [config.RESOURCES.AUDIT_MASTER],
      //     }] : [],
      //     ...(getResource(config.RESOURCES.AUDITS_PERFORMANCE, ["read"])) ? [{
      //       key: "auditperformance",
      //       path: `/${companySlug}/auditperformance`,
      //       title: "Audit Performance",
      //       permission: [config.RESOURCES.AUDITS_PERFORMANCE],
      //     }] : [],
      //     ...(getResource(config.RESOURCES.AUDIT_HEAD, ["read"])) ? [{
      //       key: "auditHead",
      //       path: `/${companySlug}/auditHead`,
      //       title: "Audit Head",
      //       permission: [config.RESOURCES.AUDIT_HEAD],
      //     }] : [],
      //   ],
      // },
      // "More" submenu (sub18)
      {
        icon: <i className="fas fa-ellipsis-h" />,
        className: "main-submenu",
        key: "sub18",
        title: "More",
        submenu: true,
        subMenuInfo: [
          ...(getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"])) ? [{
            key: "penalty-revoke",
            path: `/${companySlug}/penalty-revoke`,
            title: "sidebar.penalty-revoke",
            submenu: false,
            permission: [config.RESOURCES.ADMIN_LEAVE_MANAGEMENT],
          }] : [],
          ...(getResource(config.RESOURCES.HOLIDAYS, ["read"])) ? [{
            key: "mood-analytics",
            path: `/${companySlug}/mood-list`,
            title: "Mood Analytics",
            permission: [config.RESOURCES.HOLIDAYS],
          }] : [],
          // ...(getResource(config.RESOURCES.POLICIES, ["read"])) ? [{
          //   key: "policies",
          //   path: `/${companySlug}/policies`,
          //   title: "sidebar.policies",
          //   permission: [config.RESOURCES.POLICIES],
          // }] : [],
          // ...(getResource(config.RESOURCES.MY_GOAL, ["read"])) ? [{
          //   key: "goals",
          //   path: `/${companySlug}/goals`,
          //   title: "sidebar.goals",
          //   permission: [config.RESOURCES.MY_GOAL],
          // }] : [],
          // ...(getResource(config.RESOURCES.TEAM_GOAL, ["read"])) ? [{
          //   key: "team-goals",
          //   path: `/${companySlug}/team-goals`,
          //   title: "sidebar.team-goals",
          //   permission: [config.RESOURCES.TEAM_GOAL],
          // }] : [],
          ...(getResource(config.RESOURCES.JOB_OPENING, ["read"])) ? [{
            key: "Job Opening",
            path: `/${companySlug}/jobs/openings`,
            title: "sidebar.admin-jobopening",
            permission: [config.RESOURCES.JOB_OPENING],
          }] : [],
          ...(getResource(config.RESOURCES.INVENTORY, ["read"])) ? [{
            key: "inventory",
            path: `/${companySlug}/inventory`,
            title: "sidebar.user-assetstabpane",
            permission: [config.RESOURCES.INVENTORY],
          }] : [],
          // ...(getResource(config.RESOURCES.MANAGE_LEAVE_BALANCE, ["read"])) ? [{
          //   key: "employee-leave-balance",
          //   path: `/${companySlug}/employee-leave-balance`,
          //   title: "sidebar.employee-leave-balance",
          //   permission: [config.RESOURCES.MANAGE_LEAVE_BALANCE],
          // }] : [],
          ...(getResource(config.RESOURCES.BILLREIMBURSEMENT_APPROVAL, ["read"])) ? [
            {
              key: "Bill Reimbursement Approval",
              path: `/${companySlug}/admin-bill-reimbursement/submit`,
              title: "sidebar.admin-bill-reimbursement",
              submenu: false,
              permission: [config.RESOURCES.BILLREIMBURSEMENT_APPROVAL],
            },] : [],

        ],
      },
      // "Engage" submenu — Announcements moved out of "More"
      ...(buildEngageMenu() ? [buildEngageMenu()] : []),
      // "Survey" submenu (sub22)
      ...((getResource(config.RESOURCES["SURVEY"], ["read"]) ||
        getResource(config.RESOURCES["360QUE"], ["read"]) ||
        getResource(config.RESOURCES["360REVW"], ["read"]))
        ? [
          {
            icon: <i className="fas fa-poll" />,
            className: "main-submenu",
            key: "sub22",
            title: "Survey",
            submenu: true,
            subMenuInfo: [
              ...(getResource(config.RESOURCES["SURVEY"], ["read"])) ? [{
                key: "360_survey",
                path: `/${companySlug}/360-survey`,
                title: "sidebar.360_survey",
                permission: [config.RESOURCES["SURVEY"]],
              }] : [],
              ...(getResource(config.RESOURCES["360QUE"], ["read"])) ? [{
                key: "questionnaire",
                path: `/${companySlug}/360-questionnaire`,
                title: "sidebar.360_questionnaire",
                permission: [config.RESOURCES["360QUE"]],
              }] : [],
              ...(getResource(config.RESOURCES["360REVW"], ["read"])) ? [{
                key: "360 Reviews",
                path: `/${companySlug}/360-reviews`,
                title: "sidebar.360_reviews",
                permission: [config.RESOURCES["360REVW"]],
              }] : [],
            ],
          },
        ]
        : []),
      // Organization Tree
      {
        key: "organization-tree",
        path: `/${companySlug}/organization-view`,
        icon: <ImTree />,
        title: "sidebar.organisation-tree",
        submenu: false,
        permission: [],
      },
    ];
  };

  // Build "More" submenu based on role - different roles have different structures
  const buildMoreMenu = () => {
    // HR role has different "More" menu structure
    if (getRoles(["HR"])) {
      const hasMoreItems = getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"]) ||
        getResource(config.RESOURCES.POLICIES, ["read"]) ||
        getResource(config.RESOURCES.EMPLOYEE_GOAL, ["read"]) ||
        getResource(config.RESOURCES.TEAM_GOAL, ["read"]) ||
        getResource(config.RESOURCES.INVENTORY, ["read"]) ||
        getResource(config.RESOURCES.JOB_OPENING, ["read"]) ||
        getResource(config.RESOURCES.LEAVE_MASTER, ["read"]) ||
        getResource(config.RESOURCES.MANAGE_LEAVE_BALANCE, ["read"]) ||
        getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"]) ||
        getResource(config.RESOURCES.EMPLOYEE_DETAILS, ["read"]);

      if (!hasMoreItems) return null;

      return {
        icon: <i className="fas fa-ellipsis-h" />,
        className: " main-submenu",
        key: "sub18",
        title: "More",
        submenu: true,
        subMenuInfo: [
          ...(getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"])) ? [{
            key: "penalty-revoke",
            path: `/${companySlug}/penalty-revoke`,
            title: "sidebar.penalty-revoke",
            submenu: false,
            permission: [config.RESOURCES.ADMIN_LEAVE_MANAGEMENT],
          }] : [],
          ...(getResource(config.RESOURCES.POLICIES, ["read"])) ? [{
            key: "policies",
            path: `/${companySlug}/policies`,
            title: "sidebar.policies",
            submenu: false,
            permission: [config.RESOURCES.POLICIES],
          }] : [],
          // ...(getResource(config.RESOURCES.EMPLOYEE_GOAL, ["read"])) ? [{
          //   key: "employee-goals",
          //   path: `/${companySlug}/employee-goals`,
          //   title: "sidebar.employee-goals",
          //   submenu: false,
          //   permission: [config.RESOURCES.EMPLOYEE_GOAL],
          // }] : [],
          // ...(getResource(config.RESOURCES.TEAM_GOAL, ["read"])) ? [{
          //   key: "team-goals",
          //   path: `/${companySlug}/team-goals`,
          //   title: "sidebar.team-goals",
          //   submenu: false,
          //   permission: [config.RESOURCES.TEAM_GOAL],
          // }] : [],
          ...(getResource(config.RESOURCES.INVENTORY, ["read"])) ? [{
            key: "inventory",
            path: `/${companySlug}/inventory`,
            title: "sidebar.user-assetstabpane",
            submenu: false,
            permission: [config.RESOURCES.INVENTORY],
          }] : [],
          ...(getResource(config.RESOURCES.JOB_OPENING, ["read"])) ? [{
            key: "Job Opening",
            path: `/${companySlug}/jobs/openings`,
            title: "sidebar.admin-jobopening",
            submenu: false,
            permission: [config.RESOURCES.JOB_OPENING],
          }] : [],
          ...(getResource(config.RESOURCES.LEAVE_MASTER, ["read"])) ? [{
            key: "leave-master",
            path: `/${companySlug}/admin-leave-master`,
            title: "sidebar.admin-leave-master",
            submenu: false,
            permission: [config.RESOURCES.LEAVE_MASTER],
          }] : [],
          ...(getResource(config.RESOURCES.MANAGE_LEAVE_BALANCE, ["read"])) ? [{
            key: "employee-leave-balance",
            path: `/${companySlug}/employee-leave-balance`,
            title: "sidebar.employee-leave-balance",
            submenu: false,
            permission: [config.RESOURCES.MANAGE_LEAVE_BALANCE],
          }] : [],
          {
            key: "mood-analytics",
            path: `/${companySlug}/mood-list`,
            title: "Mood Analytics",
            submenu: false,
            permission: [], // Always shown
          },
          ...(getResource(config.RESOURCES.EMPLOYEE_DETAILS, ["read"])) ? [{
            key: "view-employee-details",
            path: `/${companySlug}/viewEmployeeDetails`,
            title: "sidebar.employee-details",
            submenu: false,
            permission: [config.RESOURCES.EMPLOYEE_DETAILS],
          }] : [],
          ...(isPlanEnabled("organization_policy") ? [{
            key: "organization-policy",
            path: `/${companySlug}/organization-policy`,
            title: "sidebar.organization-policies",
            permission: [],
          }] : []),
          {
            key: "frs-management",
            path: `/${companySlug}/frs-management`,
            title: "Face Identity Vault",
            submenu: false,
            permission: [],
          },
        ],
      };
    }

    // Account role - NO Goals in "More" menu (condition doesn't include MY_GOAL)
    if (getRoles(["Account Team"])) {
      const hasMoreItems = getResource(config.RESOURCES.INVENTORY, ["read"]) ||
        getResource(config.RESOURCES.JOB_OPENING, ["read"]) ||
        getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"]) ||
        // Bill Reimbursement Approval is listed in this menu, so it must be able to
        // open the group on its own. The sidebar's More submenu is unconditional and
        // decides visibility per item via hasVisibleSubmenuItems, so without this the
        // sidebar would show More while the header tabs had no More group at all.
        getResource(config.RESOURCES.BILLREIMBURSEMENT_APPROVAL, ["read"]) ||
        getResource(config.RESOURCES.EMPLOYEE_DETAILS, ["read"]);

      if (!hasMoreItems) return null;

      return {
        icon: <i className="fas fa-ellipsis-h" />,
        className: " main-submenu",
        key: "sub18",
        title: "More",
        submenu: true,
        subMenuInfo: [
          ...(getResource(config.RESOURCES.INVENTORY, ["read"])) ? [{
            key: "inventory",
            path: `/${companySlug}/inventory`,
            title: "sidebar.user-assetstabpane",
            submenu: false,
            permission: [config.RESOURCES.INVENTORY],
          }] : [],
          ...(getResource(config.RESOURCES.JOB_OPENING, ["read"])) ? [{
            key: "Job Opening",
            path: `/${companySlug}/jobs/openings`,
            title: "sidebar.admin-jobopening",
            submenu: false,
            permission: [config.RESOURCES.JOB_OPENING],
          }] : [],
          // ...(getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"])) ? [{
          //   key: "Bill Reimbursement",
          //   path: `/${companySlug}/bill-reimbursement`,
          //   title: "sidebar.bill-reimbursement",
          //   submenu: false,
          //   permission: [config.RESOURCES.BILLREIMBURSEMENT],
          // }] : [],
          ...(getResource(config.RESOURCES.EMPLOYEE_DETAILS, ["read"])) ? [{
            key: "view-employee-details",
            path: `/${companySlug}/viewEmployeeDetails`,
            title: "sidebar.employee-details",
            submenu: false,
            permission: [config.RESOURCES.EMPLOYEE_DETAILS],
          }] : [],
          ...(isPlanEnabled("organization_policy") ? [{
            key: "organization-policy",
            path: `/${companySlug}/organization-policy`,
            title: "sidebar.organization-policies",
            submenu: false,
            permission: [],
          }] : []),
          ...(getResource(config.RESOURCES.BILLREIMBURSEMENT_APPROVAL, ["read"])) ? [{
            key: "Bill Reimbursement Approval",
            path: `/${companySlug}/admin-bill-reimbursement/submit`,
            title: "sidebar.admin-bill-reimbursement",
            submenu: false,
            permission: [config.RESOURCES.BILLREIMBURSEMENT_APPROVAL],
          }] : [],
        ],
      };
    }

    // Auditor role - has Goals but condition doesn't include MY_GOAL in the outer check
    if (getRoles(["Auditor"])) {
      const hasMoreItems = getResource(config.RESOURCES.MY_GOAL, ["read"]) ||
        getResource(config.RESOURCES.INVENTORY, ["read"]) ||
        getResource(config.RESOURCES.JOB_OPENING, ["read"]) ||
        getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"]) ||
        getResource(config.RESOURCES.EMPLOYEE_DETAILS, ["read"]);

      if (!hasMoreItems) return null;

      return {
        icon: <i className="fas fa-ellipsis-h" />,
        className: " main-submenu",
        key: "sub18",
        title: "More",
        submenu: true,
        subMenuInfo: [
          // ...(getResource(config.RESOURCES.EMPLOYEE_GOAL, ["read"])) ? [{
          //   key: "employee-goals",
          //   path: `/${companySlug}/employee-goals`,
          //   title: "sidebar.employee-goals",
          //   submenu: false,
          //   permission: [config.RESOURCES.EMPLOYEE_GOAL],
          // }] : [],
          // ...(getResource(config.RESOURCES.TEAM_GOAL, ["read"])) ? [{
          //   key: "team-goals",
          //   path: `/${companySlug}/team-goals`,
          //   title: "sidebar.team-goals",
          //   submenu: false,
          //   permission: [config.RESOURCES.TEAM_GOAL],
          // }] : [],
          ...(getResource(config.RESOURCES.INVENTORY, ["read"])) ? [{
            key: "inventory",
            path: `/${companySlug}/inventory`,
            title: "sidebar.user-assetstabpane",
            submenu: false,
            permission: [config.RESOURCES.INVENTORY],
          }] : [],
          ...(getResource(config.RESOURCES.JOB_OPENING, ["read"])) ? [{
            key: "Job Opening",
            path: `/${companySlug}/jobs/openings`,
            title: "sidebar.admin-jobopening",
            submenu: false,
            permission: [config.RESOURCES.JOB_OPENING],
          }] : [],
          // ...(getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"])) ? [{
          //   key: "Bill Reimbursement",
          //   path: `/${companySlug}/bill-reimbursement`,
          //   title: "sidebar.bill-reimbursement",
          //   submenu: false,
          //   permission: [config.RESOURCES.BILLREIMBURSEMENT],
          // }] : [],
          ...(getResource(config.RESOURCES.EMPLOYEE_DETAILS, ["read"])) ? [{
            key: "view-employee-details",
            path: `/${companySlug}/viewEmployeeDetails`,
            title: "sidebar.employee-details",
            submenu: false,
            permission: [config.RESOURCES.EMPLOYEE_DETAILS],
          }] : [],
          ...(isPlanEnabled("organization_policy") ? [{
            key: "organization-policy",
            path: `/${companySlug}/organization-policy`,
            title: "sidebar.organization-policies",
            submenu: false,
            permission: [],
          }] : []),
        ],
      };
    }
    if (getRoles(["Reporting Manager"]) || getRoles(["Assistance Reporting Manager"])) {
      const hasMoreItems = getResource(config.RESOURCES.LEAVE_MANAGEMENT, ["read"]) ||
        getResource(config.RESOURCES.POLICIES, ["read"]) ||
        getResource(config.RESOURCES.EMPLOYEE_GOAL, ["read"]) ||
        getResource(config.RESOURCES.TEAM_GOAL, ["read"]) ||
        getResource(config.RESOURCES.INVENTORY, ["read"]) ||
        getResource(config.RESOURCES.JOB_OPENING, ["read"]) ||
        getResource(config.RESOURCES.LEAVE_MASTER, ["read"]) ||
        getResource(config.RESOURCES.MANAGE_LEAVE_BALANCE, ["read"]) ||
        getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"]) ||
        // Bill Reimbursement Approval is listed in this menu, so it must be able to
        // open the group on its own. The sidebar's More submenu is unconditional and
        // decides visibility per item via hasVisibleSubmenuItems, so without this the
        // sidebar would show More while the header tabs had no More group at all.
        getResource(config.RESOURCES.BILLREIMBURSEMENT_APPROVAL, ["read"]) ||
        getResource(config.RESOURCES.EMPLOYEE_DETAILS, ["read"]);

      if (!hasMoreItems) return null;

      return {
        icon: <i className="fas fa-ellipsis-h" />,
        className: " main-submenu",
        key: "sub18",
        title: "More",
        submenu: true,
        subMenuInfo: [
          ...(getResource(config.RESOURCES.LEAVE_MANAGEMENT, ["read"])) ? [{
            key: "user-penalty-revoke",
            path: `/${companySlug}/user-penalty-revoke`,
            title: "sidebar.penalty-revoke",
            submenu: false,
            permission: [config.RESOURCES.LEAVE_MANAGEMENT],
          }] : [],
          ...(getResource(config.RESOURCES.JOB_OPENING, ["read"])) ? [{
            key: "Job Opening",
            path: `/${companySlug}/jobs/openings`,
            title: "sidebar.admin-jobopening",
            submenu: false,
            permission: [config.RESOURCES.JOB_OPENING],
          }] : [],
          // ...(getResource(config.RESOURCES.TEAM_GOAL, ["read"])) ? [{
          //   key: "team-goals",
          //   path: `/${companySlug}/team-goals`,
          //   title: "sidebar.team-goals",
          //   submenu: false,
          //   permission: [config.RESOURCES.TEAM_GOAL],
          // }] : [],

          ...(getResource(config.RESOURCES.INVENTORY, ["read"])) ? [{
            key: "inventory",
            path: `/${companySlug}/inventory`,
            title: "sidebar.user-assetstabpane",
            submenu: false,
            permission: [config.RESOURCES.INVENTORY],
          }] : [],
          ...(getResource(config.RESOURCES.EMPLOYEE_DETAILS, ["read"])) ? [
            {
              key: "view-employee-details",
              path: `/${companySlug}/viewEmployeeDetails`,
              title: "sidebar.employee-details",
              submenu: false,
              permission: [config.RESOURCES.EMPLOYEE_DETAILS],
            }] : [],
          ...(getResource(config.RESOURCES.BILLREIMBURSEMENT_APPROVAL, ["read"])) ? [
            {
              key: "Bill Reimbursement Approval",
              path: `/${companySlug}/admin-bill-reimbursement/submit`,
              title: "sidebar.admin-bill-reimbursement",
              submenu: false,
              permission: [config.RESOURCES.BILLREIMBURSEMENT_APPROVAL],
            },] : [],
          ...(isPlanEnabled("organization_policy") ? [{
            key: "organization-policy",
            path: `/${companySlug}/organization-policy`,
            title: "sidebar.organization-policies",
            submenu: false,
            permission: [],
          }] : []),
        ],
      };
    }

    // User, Reporting Manager, Assistant Reporting Manager, Network Team - includes MY_GOAL in condition
    const hasMoreItems = getResource(config.RESOURCES.JOB_OPENING, ["read"]) ||
      getResource(config.RESOURCES.MY_GOAL, ["read"]) ||
      getResource(config.RESOURCES.INVENTORY, ["read"]) ||
      getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_DETAILS, ["read"]);

    if (!hasMoreItems) return null;

    return {
      icon: <i className="fas fa-ellipsis-h" />,
      className: "main-submenu",
      key: "sub18",
      title: "More",
      submenu: true,
      subMenuInfo: [
        // ...(getResource(config.RESOURCES.EMPLOYEE_GOAL, ["read"])) ? [{
        //   key: "employee-goals",
        //   path: `/${companySlug}/employee-goals`,
        //   title: "sidebar.employee-goals",
        //   submenu: false,
        //   permission: [config.RESOURCES.EMPLOYEE_GOAL],
        // }] : [],
        // ...(getResource(config.RESOURCES.TEAM_GOAL, ["read"])) ? [{
        //   key: "team-goals",
        //   path: `/${companySlug}/team-goals`,
        //   title: "sidebar.team-goals",
        //   submenu: false,
        //   permission: [config.RESOURCES.TEAM_GOAL],
        // }] : [],
        ...(getResource(config.RESOURCES.INVENTORY, ["read"])) ? [{
          key: "inventory",
          path: `/${companySlug}/inventory`,
          title: "sidebar.user-assetstabpane",
          submenu: false,
          permission: [config.RESOURCES.INVENTORY],
        }] : [],
        ...(getResource(config.RESOURCES.JOB_OPENING, ["read"])) ? [{
          key: "Job Opening",
          path: `/${companySlug}/jobs/openings`,
          title: "sidebar.admin-jobopening",
          submenu: false,
          permission: [config.RESOURCES.JOB_OPENING],
        }] : [],
        ...(getResource(config.RESOURCES.EMPLOYEE_DETAILS, ["read"])) ? [{
          key: "view-employee-details",
          path: `/${companySlug}/viewEmployeeDetails`,
          title: "sidebar.employee-details",
          submenu: false,
          permission: [config.RESOURCES.EMPLOYEE_DETAILS],
        }] : [],
        ...(isPlanEnabled("organization_policy") ? [{
          key: "organization-policy",
          path: `/${companySlug}/organization-policy`,
          title: "sidebar.organization-policies",
          submenu: false,
          permission: [],
        }] : []),
      ],
    };
  };

  // Build HR-specific menu items (Management submenu)
  const buildHRMenuItems = () => {
    if (!getRoles(["HR"])) return [];

    const hasManagementItems = getResource(config.RESOURCES.ACHIEVEMENTS, ["read"]) ||
      getResource(config.RESOURCES.SETTINGS, ["read"]) ||
      getResource(config.RESOURCES.DEPARTMENTS, ["read"]) ||
      getResource(config.RESOURCES.DESIGNATION, ["read"]) ||
      getResource(config.RESOURCES.HOLIDAYS, ["read"]) ||
      getResource(config.RESOURCES.SHIFT, ["read"]);

    if (!hasManagementItems) return [];

    return [{
      icon: <PermissionmanagerIcon />,
      className: "main-submenu",
      key: "sub17",
      title: "Management",
      submenu: true,
      subMenuInfo: [
        ...(getResource(config.RESOURCES.ACHIEVEMENTS, ["read"])) ? [{
          key: "achivements",
          path: `/${companySlug}/achivements`,
          title: "sidebar.achivements",
          submenu: false,
          permission: [config.RESOURCES.ACHIEVEMENTS],
        }] : [],
        ...(getResource(config.RESOURCES.SETTINGS, ["read"])) ? [
          {
            key: "permission",
            path: `/${companySlug}/permission-manager`,
            title: "sidebar.permission",
            submenu: false,
            permission: [config.RESOURCES.SETTINGS],
          },
          ...(isPlanEnabled("zoho_payroll_integration") ? [{
            key: "zoho-payroll-integration",
            path: `/${companySlug}/admin/zoho-payroll-integration`,
            title: "Zoho Payroll Integration",
            submenu: false,
            permission: [config.RESOURCES.SETTINGS],
          }] : []),
        ] : [],
        ...(getResource(config.RESOURCES.DEPARTMENTS, ["read"])) ? [{
          key: "admin-department",
          path: `/${companySlug}/admin-department`,
          title: "sidebar.Department",
          submenu: false,
          permission: [config.RESOURCES.DEPARTMENTS],
        }] : [],
        ...(getResource(config.RESOURCES.DESIGNATION, ["read"])) ? [{
          key: "admin-designation",
          path: `/${companySlug}/admin-designation`,
          title: "sidebar.designation",
          submenu: false,
          permission: [config.RESOURCES.DESIGNATION],
        }] : [],
        ...(getResource(config.RESOURCES.SHIFT, ["read"])) ? [{
          key: "admin-shift",
          path: `/${companySlug}/admin-shift`,
          title: "sidebar.shift",
          submenu: false,
          permission: [config.RESOURCES.SHIFT],
        }] : [],
        ...(getResource(config.RESOURCES.BUSINESS_UNIT, ["read"])) ? [{
          key: "admin-businessunit",
          path: `/${companySlug}/admin-businessunit`,
          title: "sidebar.businessunit",
          submenu: false,
          permission: [config.RESOURCES.BUSINESS_UNIT],
        }] : [],
        ...(getResource(config.RESOURCES.SUBDEPARTMENT, ["read"])) ? [{
          key: "admin-subdepartment",
          path: `/${companySlug}/admin-subdepartment`,
          title: "sidebar.subdepartment",
          submenu: false,
          permission: [config.RESOURCES.SUBDEPARTMENT],
        }] : [],
        ...(getResource(config.RESOURCES.EMPLOYEMENT_TYPE, ["read"])) ? [{
          key: "admin-employment-type",
          path: `/${companySlug}/admin-employment-type`,
          title: "Employment Type",
          submenu: false,
          permission: [config.RESOURCES.EMPLOYEMENT_TYPE],
        }] : [],
        ...(getResource(config.RESOURCES.HOLIDAYS, ["read"])) ? [{
          key: "holidays",
          path: `/${companySlug}/holidays`,
          title: "sidebar.holidays",
          submenu: false,
          permission: [config.RESOURCES.HOLIDAYS],
        }] : [],
        ...(getResource(config.RESOURCES.SHIFT, ["read"])) ? [{
          key: "admin-roster",
          path: `/${companySlug}/admin-roster`,
          title: "sidebar.roster",
          submenu: false,
          permission: [config.RESOURCES.SHIFT],
        }] : [],
      ],
    }];
  };

  // Build Recruiter-specific menu items (Management submenu) - same as HR
  const buildRecruiterManagementMenuItems = () => {
    if (!getRoles(["Recruiter"])) return [];

    const hasManagementItems = getResource(config.RESOURCES.ACHIEVEMENTS, ["read"]) ||
      getResource(config.RESOURCES.SETTINGS, ["read"]) ||
      getResource(config.RESOURCES.DEPARTMENTS, ["read"]) ||
      getResource(config.RESOURCES.DESIGNATION, ["read"]) ||
      getResource(config.RESOURCES.HOLIDAYS, ["read"]) ||
      getResource(config.RESOURCES.SHIFT, ["read"]);

    if (!hasManagementItems) return [];

    return [{
      icon: <PermissionmanagerIcon />,
      className: "main-submenu",
      key: "sub17",
      title: "Management",
      submenu: true,
      subMenuInfo: [
        ...(getResource(config.RESOURCES.ACHIEVEMENTS, ["read"])) ? [{
          key: "achivements",
          path: `/${companySlug}/achivements`,
          title: "sidebar.achivements",
          submenu: false,
          permission: [config.RESOURCES.ACHIEVEMENTS],
        }] : [],
        ...(getResource(config.RESOURCES.SETTINGS, ["read"])) ? [
          {
            key: "permission",
            path: `/${companySlug}/permission-manager`,
            title: "sidebar.permission",
            submenu: false,
            permission: [config.RESOURCES.SETTINGS],
          },
          ...(isPlanEnabled("zoho_payroll_integration") ? [{
            key: "zoho-payroll-integration",
            path: `/${companySlug}/admin/zoho-payroll-integration`,
            title: "Zoho Payroll Integration",
            submenu: false,
            permission: [config.RESOURCES.SETTINGS],
          }] : []),
        ] : [],
        ...(getResource(config.RESOURCES.DEPARTMENTS, ["read"])) ? [{
          key: "admin-department",
          path: `/${companySlug}/admin-department`,
          title: "sidebar.Department",
          submenu: false,
          permission: [config.RESOURCES.DEPARTMENTS],
        }] : [],
        ...(getResource(config.RESOURCES.DESIGNATION, ["read"])) ? [{
          key: "admin-designation",
          path: `/${companySlug}/admin-designation`,
          title: "sidebar.designation",
          submenu: false,
          permission: [config.RESOURCES.DESIGNATION],
        }] : [],
        ...(getResource(config.RESOURCES.SHIFT, ["read"])) ? [{
          key: "admin-shift",
          path: `/${companySlug}/admin-shift`,
          title: "sidebar.shift",
          submenu: false,
          permission: [config.RESOURCES.SHIFT],
        }] : [],
        ...(getResource(config.RESOURCES.BUSINESS_UNIT, ["read"])) ? [{
          key: "admin-businessunit",
          path: `/${companySlug}/admin-businessunit`,
          title: "sidebar.businessunit",
          submenu: false,
          permission: [config.RESOURCES.BUSINESS_UNIT],
        }] : [],
        ...(getResource(config.RESOURCES.SUBDEPARTMENT, ["read"])) ? [{
          key: "admin-subdepartment",
          path: `/${companySlug}/admin-subdepartment`,
          title: "sidebar.subdepartment",
          submenu: false,
          permission: [config.RESOURCES.SUBDEPARTMENT],
        }] : [],
        ...(getResource(config.RESOURCES.EMPLOYEMENT_TYPE, ["read"])) ? [{
          key: "admin-employment-type",
          path: `/${companySlug}/admin-employment-type`,
          title: "Employment Type",
          submenu: false,
          permission: [config.RESOURCES.EMPLOYEMENT_TYPE],
        }] : [],
        ...(getResource(config.RESOURCES.HOLIDAYS, ["read"])) ? [{
          key: "holidays",
          path: `/${companySlug}/holidays`,
          title: "sidebar.holidays",
          submenu: false,
          permission: [config.RESOURCES.HOLIDAYS],
        }] : [],
        ...(getResource(config.RESOURCES.SHIFT, ["read"])) ? [{
          key: "admin-roster",
          path: `/${companySlug}/admin-roster`,
          title: "sidebar.roster",
          submenu: false,
          permission: [config.RESOURCES.SHIFT],
        }] : [],
      ],
    }];
  };

  // Build Intern menu array - matches JSX structure from SidebarContent
  const buildInternMenu = () => {
    return [
      {
        key: "User Dashboard",
        path: `/${companySlug}/dashboard`,
        icon: <AiOutlineDashboard />,
        title: "sidebar.User_Dashboard",
        submenu: false,
        permission: [],
      },
      // "Me" submenu
      ...(getResource(config.RESOURCES.EMPLOYEE_IN_OUT, ["read"]) ||
        getResource(config.RESOURCES.LEAVE, ["read"]) ||
        getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"]) ||
        getResource(config.RESOURCES.SEPARATION, ["read"]) ||
        getResource(config.RESOURCES.TICKET, ["read"]) ||
        getResource(config.RESOURCES.MY_ASSETS, ["read"]) ||
        getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"]) ||
        getResource(config.RESOURCES.RANKING, ["read"]) ||
        getResource(config.RESOURCES.MY_GOAL, ["read"]) ||
        getResource(config.RESOURCES.AUDITS, ["read"]) ||
        getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"])
        ? [
          {
            key: "Me",
            title: "Me",
            className: " main-submenu",
            icon: <i className="fas fa-user " />,
            submenu: true,
            subMenuInfo: [
              ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])) ? [{
                key: "My-In-Out",
                path: `/${companySlug}/my-in-out`,
                title: "sidebar.My-In-Out",
                submenu: false,
                permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
              }] : [],
              ...(getResource(config.RESOURCES.LEAVE, ["read"])) ? [{
                key: "manage-leave-application",
                path: `/${companySlug}/leave-application`,
                title: "sidebar.leave-application",
                permission: [config.RESOURCES.LEAVE],
              }] : [],
              ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])) ? [{
                key: "attendance-regularization",
                path: `/${companySlug}/employee-attendence-regularization`,
                title: "sidebar.Attendence_Regularization",
                permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
              }] : [],
              ...(getResource(config.RESOURCES.SEPARATION, ["read"])) ? [{
                key: "resignation",
                path: `/${companySlug}/resignation`,
                title: "sidebar.resignation",
                permission: [config.RESOURCES.SEPARATION],
              }] : [],
              ...(getResource(config.RESOURCES.TICKET, ["read"])) ? [{
                key: "tickets",
                path: `/${companySlug}/tickets`,
                title: "sidebar.tickets",
                permission: [config.RESOURCES.TICKET],
              }] : [],
              ...(getResource(config.RESOURCES.MY_ASSETS, ["read"])) ? [{
                key: "assets",
                path: `/${companySlug}/user-assets`,
                title: "sidebar.user-assets",
                permission: [config.RESOURCES.MY_ASSETS],
              }] : [],
              ...(getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"])) ? [{
                key: "user-reports",
                path: `/${companySlug}/user-reports`,
                title: "sidebar.user-reports",
                permission: [config.RESOURCES.ATTENDANCE_REPORT],
              }] : [],
              ...(getResource(config.RESOURCES.RANKING, ["read"])) ? [{
                key: "individual-ranking",
                path: `/${companySlug}/individual-rank`,
                title: "sidebar.individual-rank",
                permission: [config.RESOURCES.RANKING],
              }] : [],
              // ...(getResource(config.RESOURCES.MY_GOAL, ["read"])) ? [{
              //   key: "goals",
              //   path: `/${companySlug}/my-goals`,
              //   title: "sidebar.create-goals",
              //   permission: [config.RESOURCES.MY_GOAL],
              // }] : [],
              // ...(getResource(config.RESOURCES.AUDITS, ["read"])) ? [{
              //   key: "audits",
              //   path: `/${companySlug}/my-audit-history`,
              //   title: "Audits",
              //   permission: [config.RESOURCES.AUDITS],
              // }] : [],
              // Project Overview - always shown for Intern role (when Me submenu exists)
              // {
              //   key: "proejct-overview",
              //   path: `/${companySlug}/project-overview`,
              //   title: "sidebar.project-overview",
              //   permission: [],
              // },
              ...(getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"])) ? [
                {
                  key: "Bill Reimbursement",
                  path: `/${companySlug}/bill-reimbursement`,
                  title: "sidebar.bill-reimbursement",
                  submenu: false,
                  permission: [config.RESOURCES.BILLREIMBURSEMENT],
                }] : [],
            ],
          },
        ]
        : []),
      // "Team" submenu (sub16) - Same structure as Admin
      ...(getResource(config.RESOURCES.EMPLOYEE_MASTER, ["read"]) ||
        getResource(config.RESOURCES.EMPLOYEE_IN_OUT, ["read"]) ||
        getResource(config.RESOURCES.SEPARATION_MANAGEMENT, ["read"]) ||
        getResource(config.RESOURCES.EMPLOYEE_REPORT, ["read"]) ||
        getResource(config.RESOURCES.FINANCE_REPORT, ["read"]) ||
        getResource(config.RESOURCES.EMPLOYEE_TICKET, ["read"]) ||
        getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"]) ||
        getResource(config.RESOURCES.LEAVE_MANAGEMENT, ["read"]) ||
        getResource(config.RESOURCES.ADMIN_REGULARIZATION, ["read"]) ||
        getResource(config.RESOURCES.TEAM_REGULARIZATION, ["read"]) ||
        getResource(config.RESOURCES.KPI, ["read"]) ||
        getResource(config.RESOURCES.LAST_WORKING_DAY_LIST, ["read"])
        ? [
          {
            icon: <TeamInfoIcon />,
            className: "main-submenu",
            key: "sub16",
            title: "Team",
            submenu: true,
            subMenuInfo: [
              ...(getResource(config.RESOURCES.EMPLOYEE_MASTER, ["read"])) ? [{
                key: "employee-master",
                path: `/${companySlug}/employee-master`,
                title: "sidebar.employee-master",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_MASTER],
              }] : [],
              ...(getResource(config.RESOURCES.EMPLOYEE_IN_OUT, ["read"])) ? [{
                key: "employee-in-out",
                path: `/${companySlug}/employee-in-out`,
                title: "sidebar.Employee-In-Out",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_IN_OUT],
              }] : [],
              ...(getResource(config.RESOURCES.SEPARATION_MANAGEMENT, ["read"])) ? [{
                key: "resignation-employee-list",
                path: `/${companySlug}/resignation-employee-list`,
                title: "sidebar.resignation-employee-list",
                submenu: false,
                permission: [config.RESOURCES.SEPARATION_MANAGEMENT],
              }] : [],
              ...(getResource(config.RESOURCES.LAST_WORKING_DAY_LIST, ["read"])) ? [{
                key: "last-working-day-emps",
                path: `/${companySlug}/last-working-day-emps`,
                title: "Last Working Day List",
                permission: [config.RESOURCES.LAST_WORKING_DAY_LIST],
              }] : [],
              ...(getResource(config.RESOURCES.EMPLOYEE_REPORT, ["read"]) ||
                getResource(config.RESOURCES.LEAVE_REPORT, ["read"]) ||
                getResource(config.RESOURCES.FINANCE_REPORT, ["read"]) ||
                getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"])) ? [{
                key: "admin-reports",
                path: `/${companySlug}/admin-reports`,
                title: "sidebar.admin-reports",
                submenu: false,
                permission: [
                  config.RESOURCES.EMPLOYEE_REPORT,
                  config.RESOURCES.LEAVE_REPORT,
                  config.RESOURCES.ATTENDANCE_REPORT,
                  config.RESOURCES.FINANCE_REPORT,
                ],
              }] : [],
              ...(getResource(config.RESOURCES.EMPLOYEE_TICKET, ["read"])) ? [{
                key: "tickets",
                path: `/${companySlug}/employee-tickets`,
                title: "sidebar.tickets",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_TICKET],
              }] : [],
              ...(getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"])) ? [{
                key: "admin-leave-approval",
                path: `/${companySlug}/admin-leave-approval`,
                title: "sidebar.admin-leave-management",
                submenu: false,
                permission: [config.RESOURCES.ADMIN_LEAVE_MANAGEMENT],
              }] : [],
              ...(getResource(config.RESOURCES.LEAVE_MANAGEMENT, ["read"])) ? [{
                key: "user-leave-approval",
                path: `/${companySlug}/user-leave-approve`,
                title: "sidebar.user-leave-management",
                submenu: false,
                permission: [config.RESOURCES.LEAVE_MANAGEMENT],
              }] : [],
              ...(getResource(config.RESOURCES.ADMIN_REGULARIZATION, ["read"])) ? [{
                key: "admin-regularizatrion",
                path: `/${companySlug}/admin-regularizatrion`,
                title: "sidebar.admin-regularizatrion",
                submenu: false,
                permission: [config.RESOURCES.ADMIN_REGULARIZATION],
              }] : [],
              ...(getResource(config.RESOURCES.TEAM_REGULARIZATION, ["read"])) ? [{
                key: "ManageTeamRegularization",
                path: `/${companySlug}/manage-team-regularization`,
                title: "sidebar.ManageTeamRegularization",
                submenu: false,
                permission: [config.RESOURCES.TEAM_REGULARIZATION],
              }] : [],
              // ...(getResource(config.RESOURCES.KPI, ["read"])) ? [{
              //   key: "Kpis",
              //   path: `/${companySlug}/Kpis`,
              //   title: "sidebar.manage-kpis",
              //   submenu: false,
              //   permission: [config.RESOURCES.KPI],
              // }] : [],
            ],
          },
        ]
        : []),
      // Performance Review submenu
      ...(performanceMenu.subMenuInfo.length > 0 ? [performanceMenu] : []),

      // Learning (LMS) submenu
      ...(hasAnyLmsPermission() ? [learningMenu] : []),

      // Enhanced Expense Management submenu
      ...(expenseMenu.subMenuInfo.length > 0 ? [expenseMenu] : []),
      ...(skillsMenu.subMenuInfo.length > 0 ? [skillsMenu] : []),
      ...(careerPathMenu.subMenuInfo.length > 0 ? [careerPathMenu] : []),

      // "Onboarding" submenu (sub19)
      ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"]) ||
        getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"]) ||
        getResource(config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST, ["read"]) ||
        getResource(config.RESOURCES.NEW_JOINEES, ["read"])
        ? [
          {
            icon: <OnboardingIcon />,
            className: "main-submenu",
            key: "sub19",
            title: "Onboarding",
            submenu: true,
            subMenuInfo: [
              ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"])) ? [{
                key: "company-doc-master",
                path: `/${companySlug}/company-doc-master`,
                title: "sidebar.docMaster",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_INITIATION],
              }] : [],
              ...(getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"])) ? [{
                key: "candidate-approvel",
                path: `/${companySlug}/candidate-approval`,
                title: "sidebar.candidate-approvel",
                submenu: false,
                permission: [config.RESOURCES.CANDIDATE_APPROVAL],
              }] : [],
              ...(getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"])) ? [{
                key: "candidate-list",
                path: `/${companySlug}/candidate-list`,
                title: "sidebar.candidate-approvel-list",
                submenu: false,
                permission: [config.RESOURCES.CANDIDATE_APPROVAL],
              }] : [],
              ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"])) ? [{
                key: "initiation",
                path: `/${companySlug}/onboarding-initiation`,
                title: "sidebar.onboarding-initiation",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_INITIATION],
              }] : [],
              ...(getResource(config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST, ["read"])) ? [{
                key: "doc-approve-list",
                path: `/${companySlug}/doc-approve-list`,
                title: "sidebar.doc-approve-list",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST],
              }] : [],
              ...(getResource(config.RESOURCES.NEW_JOINEES, ["read"])) ? [{
                key: "new-joinees-list",
                path: `/${companySlug}/new-joinees-list`,
                title: "sidebar.new-joinees-list",
                submenu: false,
                permission: [config.RESOURCES.NEW_JOINEES],
              }] : [],
            ],
          },
        ]
        : []),
      // "Audit" submenu (sub20) - Same structure as Admin
      // ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"]) ||
      //   getResource(config.RESOURCES.AUDITS_PERFORMANCE, ["read"]) ||
      //   getResource(config.RESOURCES.AUDIT_HEAD, ["read"])
      //   ? [
      //     {
      //       icon: <AuditIcon />,
      //       className: "main-submenu",
      //       key: "sub20",
      //       title: "Audit",
      //       submenu: true,
      //       subMenuInfo: [
      //         ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"])) ? [{
      //           key: "Audit Master",
      //           path: `/${companySlug}/auditmaster`,
      //           title: "Audits Master",
      //           submenu: false,
      //           permission: [config.RESOURCES.AUDIT_MASTER],
      //         }] : [],
      //         ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"])) ? [{
      //           key: "Audits",
      //           path: `/${companySlug}/audits`,
      //           title: "Audits",
      //           submenu: false,
      //           permission: [config.RESOURCES.AUDIT_MASTER],
      //         }] : [],
      //         ...(getResource(config.RESOURCES.AUDITS_PERFORMANCE, ["read"])) ? [{
      //           key: "auditperformance",
      //           path: `/${companySlug}/auditperformance`,
      //           title: "Audit Performance",
      //           submenu: false,
      //           permission: [config.RESOURCES.AUDITS_PERFORMANCE],
      //         }] : [],
      //         ...(getResource(config.RESOURCES.AUDIT_HEAD, ["read"])) ? [{
      //           key: "auditHead",
      //           path: `/${companySlug}/auditHead`,
      //           title: "Audit Head",
      //           submenu: false,
      //           permission: [config.RESOURCES.AUDIT_HEAD],
      //         }] : [],
      //       ],
      //     },
      //   ]
      //   : []),
      // Standalone KPI item
      ...(getResource(config.RESOURCES["KPI"], ["read"])) ? [{
        key: "Kpis",
        path: `/${companySlug}/user-kpi`,
        icon: <FundOutlined />,
        title: "sidebar.kpis",
        submenu: false,
        permission: [],
      }] : [],
      // "Survey" submenu (sub22) - Same structure as Admin
      ...((getResource(config.RESOURCES["SURVEY"], ["read"]) ||
        getResource(config.RESOURCES["360QUE"], ["read"]) ||
        getResource(config.RESOURCES["360REVW"], ["read"]))
        ? [
          {
            icon: <i className="fas fa-poll" />,
            className: "main-submenu",
            key: "sub22",
            title: "Survey",
            submenu: true,
            subMenuInfo: [
              ...(getResource(config.RESOURCES["SURVEY"], ["read"])) ? [{
                key: "360_survey",
                path: `/${companySlug}/360-survey`,
                title: "sidebar.360_survey",
                submenu: false,
                permission: [config.RESOURCES["SURVEY"]],
              }] : [],
              ...(getResource(config.RESOURCES["360QUE"], ["read"])) ? [{
                key: "360_questionnaire",
                path: `/${companySlug}/360-questionnaire`,
                title: "sidebar.360_questionnaire",
                submenu: false,
                permission: [config.RESOURCES["360QUE"]],
              }] : [],
              ...(getResource(config.RESOURCES["360REVW"], ["read"])) ? [{
                key: "360_reviews",
                path: `/${companySlug}/360-reviews`,
                title: "sidebar.360_reviews",
                submenu: false,
                permission: [config.RESOURCES["360REVW"]],
              }] : [],
            ],
          },
        ]
        : []),
      // "More" submenu (sub18)
      {
        icon: <i className="fas fa-ellipsis-h" />,
        className: "main-submenu",
        key: "sub18",
        title: "More",
        submenu: true,
        subMenuInfo: [
          ...(getResource(config.RESOURCES.JOB_OPENING, ["read"])) ? [{
            key: "Job Opening",
            path: `/${companySlug}/jobs/openings`,
            title: "sidebar.admin-jobopening",
            submenu: false,
            permission: [config.RESOURCES.JOB_OPENING],
          }] : [],
          // ...(getResource(config.RESOURCES.EMPLOYEE_GOAL, ["read"])) ? [{
          //   key: "employee-goals",
          //   path: `/${companySlug}/employee-goals`,
          //   title: "sidebar.employee-goals",
          //   submenu: false,
          //   permission: [config.RESOURCES.EMPLOYEE_GOAL],
          // }] : [],
          // ...(getResource(config.RESOURCES.TEAM_GOAL, ["read"])) ? [{
          //   key: "team-goals",
          //   path: `/${companySlug}/team-goals`,
          //   title: "sidebar.team-goals",
          //   submenu: false,
          //   permission: [config.RESOURCES.TEAM_GOAL],
          // }] : [],
          ...(getResource(config.RESOURCES.INVENTORY, ["read"])) ? [{
            key: "inventory",
            path: `/${companySlug}/inventory`,
            title: "sidebar.user-assetstabpane",
            submenu: false,
            permission: [config.RESOURCES.INVENTORY],
          }] : [],
          ...(getResource(config.RESOURCES.EMPLOYEE_DETAILS, ["read"])) ? [{
            key: "view-employee-details",
            path: `/${companySlug}/viewEmployeeDetails`,
            title: "sidebar.employee-details",
            submenu: false,
            permission: [config.RESOURCES.EMPLOYEE_DETAILS],
          }] : [],
          ...(isPlanEnabled("organization_policy") ? [{
            key: "organization-policy",
            path: `/${companySlug}/organization-policy`,
            title: "sidebar.organization-policies",
            submenu: false,
            permission: [],
          }] : []),
        ],
      },
      // Organization Tree
      {
        key: "organization-tree",
        path: `/${companySlug}/organization-view`,
        icon: <ImTree />,
        title: "sidebar.organisation-tree",
        submenu: false,
        permission: [],
      },
    ];
  };

  // Build Recruiter menu array - matches JSX structure from SidebarContent
  const buildRecruiterMenu = () => {
    return [
      {
        key: "User Dashboard",
        path: `/${companySlug}/dashboard`,
        icon: <AiOutlineDashboard />,
        title: "sidebar.User_Dashboard",
        submenu: false,
        permission: [],
      },
      // "Me" submenu
      ...(getResource(config.RESOURCES.EMPLOYEE_IN_OUT, ["read"]) ||
        getResource(config.RESOURCES.LEAVE, ["read"]) ||
        getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"]) ||
        getResource(config.RESOURCES.SEPARATION, ["read"]) ||
        getResource(config.RESOURCES.TICKET, ["read"]) ||
        getResource(config.RESOURCES.MY_ASSETS, ["read"]) ||
        getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"]) ||
        getResource(config.RESOURCES.RANKING, ["read"]) ||
        getResource(config.RESOURCES.MY_GOAL, ["read"]) ||
        getResource(config.RESOURCES.AUDITS, ["read"]) ||
        getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"])
        ? [
          {
            key: "Me",
            title: "Me",
            className: " main-submenu",
            icon: <i className="fas fa-user " />,
            submenu: true,
            subMenuInfo: [
              ...(getResource(config.RESOURCES.EMPLOYEE_IN_OUT, ["read"])) ? [{
                key: "My-In-Out",
                path: `/${companySlug}/my-in-out`,
                title: "sidebar.My-In-Out",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_IN_OUT],
              }] : [],
              ...(getResource(config.RESOURCES.LEAVE, ["read"])) ? [{
                key: "manage-leave-application",
                path: `/${companySlug}/leave-application`,
                title: "sidebar.leave-application",
                permission: [config.RESOURCES.LEAVE],
              }] : [],
              ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])) ? [{
                key: "attendance-regularization",
                path: `/${companySlug}/employee-attendence-regularization`,
                title: "sidebar.Attendence_Regularization",
                permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
              }] : [],
              ...(getResource(config.RESOURCES.SEPARATION, ["read"])) ? [{
                key: "resignation",
                path: `/${companySlug}/resignation`,
                title: "sidebar.resignation",
                permission: [config.RESOURCES.SEPARATION],
              }] : [],
              ...(getResource(config.RESOURCES.TICKET, ["read"])) ? [{
                key: "tickets",
                path: `/${companySlug}/tickets`,
                title: "sidebar.tickets",
                permission: [config.RESOURCES.TICKET],
              }] : [],
              ...(getResource(config.RESOURCES.MY_ASSETS, ["read"])) ? [{
                key: "assets",
                path: `/${companySlug}/user-assets`,
                title: "sidebar.user-assets",
                permission: [config.RESOURCES.MY_ASSETS],
              }] : [],
              ...(getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"])) ? [{
                key: "user-reports",
                path: `/${companySlug}/user-reports`,
                title: "sidebar.user-reports",
                permission: [config.RESOURCES.ATTENDANCE_REPORT],
              }] : [],
              ...(getResource(config.RESOURCES.RANKING, ["read"])) ? [{
                key: "individual-ranking",
                path: `/${companySlug}/individual-rank`,
                title: "sidebar.individual-rank",
                permission: [config.RESOURCES.RANKING],
              }] : [],
              // ...(getResource(config.RESOURCES.MY_GOAL, ["read"])) ? [{
              //   key: "goals",
              //   path: `/${companySlug}/my-goals`,
              //   title: "sidebar.create-goals",
              //   permission: [config.RESOURCES.MY_GOAL],
              // }] : [],
              // ...(getResource(config.RESOURCES.AUDITS, ["read"])) ? [{
              //   key: "audits",
              //   path: `/${companySlug}/my-audit-history`,
              //   title: "Audits",
              //   permission: [config.RESOURCES.AUDITS],
              // }] : [],
              // Project Overview - always shown for Recruiter role (when Me submenu exists)
              // {
              //   key: "proejct-overview",
              //   path: `/${companySlug}/project-overview`,
              //   title: "sidebar.project-overview",
              //   permission: [],
              // },
              ...(getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"])) ? [
                {
                  key: "Bill Reimbursement",
                  path: `/${companySlug}/bill-reimbursement`,
                  title: "sidebar.bill-reimbursement",
                  submenu: false,
                  permission: [config.RESOURCES.BILLREIMBURSEMENT],
                }] : [],
            ],
          },
        ]
        : []),
      // "Team" submenu
      ...(getResource(config.RESOURCES.EMPLOYEE_MASTER, ["read"]) ||
        getResource(config.RESOURCES.EMPLOYEE_IN_OUT, ["read"]) ||
        getResource(config.RESOURCES.SEPARATION_MANAGEMENT, ["read"]) ||
        getResource(config.RESOURCES.EMPLOYEE_REPORT, ["read"]) ||
        getResource(config.RESOURCES.FINANCE_REPORT, ["read"]) ||
        getResource(config.RESOURCES.EMPLOYEE_TICKET, ["read"]) ||
        getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"]) ||
        getResource(config.RESOURCES.LEAVE_MANAGEMENT, ["read"]) ||
        getResource(config.RESOURCES.ADMIN_REGULARIZATION, ["read"]) ||
        getResource(config.RESOURCES.TEAM_REGULARIZATION, ["read"]) ||
        getResource(config.RESOURCES.KPI, ["read"]) ||
        getResource(config.RESOURCES.LAST_WORKING_DAY_LIST, ["read"])
        ? [
          {
            icon: <TeamInfoIcon />,
            className: "main-submenu",
            key: "sub16",
            title: "Team",
            submenu: true,
            subMenuInfo: [
              ...(getResource(config.RESOURCES.EMPLOYEE_MASTER, ["read"])) ? [{
                key: "employee-master",
                path: `/${companySlug}/employee-master`,
                title: "sidebar.employee-master",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_MASTER],
              }] : [],
              ...(getResource(config.RESOURCES.EMPLOYEE_IN_OUT, ["read"])) ? [{
                key: "employee-in-out",
                path: `/${companySlug}/employee-in-out`,
                title: "sidebar.Employee-In-Out",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_IN_OUT],
              }] : [],
              ...(getResource(config.RESOURCES.SEPARATION_MANAGEMENT, ["read"])) ? [{
                key: "resignation-employee-list",
                path: `/${companySlug}/resignation-employee-list`,
                title: "sidebar.resignation-employee-list",
                submenu: false,
                permission: [config.RESOURCES.SEPARATION_MANAGEMENT],
              }] : [],
              ...(getResource(config.RESOURCES.LAST_WORKING_DAY_LIST, ["read"])) ? [{
                key: "last-working-day-emps",
                path: `/${companySlug}/last-working-day-emps`,
                title: "Last Working Day List",
                permission: [config.RESOURCES.LAST_WORKING_DAY_LIST],
              }] : [],
              ...(getResource(config.RESOURCES.EMPLOYEE_REPORT, ["read"]) ||
                getResource(config.RESOURCES.LEAVE_REPORT, ["read"]) ||
                getResource(config.RESOURCES.FINANCE_REPORT, ["read"]) ||
                getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"])) ? [{
                key: "admin-reports",
                path: `/${companySlug}/admin-reports`,
                title: "sidebar.admin-reports",
                submenu: false,
                permission: [
                  config.RESOURCES.EMPLOYEE_REPORT,
                  config.RESOURCES.LEAVE_REPORT,
                  config.RESOURCES.ATTENDANCE_REPORT,
                  config.RESOURCES.FINANCE_REPORT,
                ],
              }] : [],
              ...(getResource(config.RESOURCES.EMPLOYEE_TICKET, ["read"])) ? [{
                key: "tickets",
                path: `/${companySlug}/employee-tickets`,
                title: "sidebar.tickets",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_TICKET],
              }] : [],
              ...(getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"])) ? [{
                key: "admin-leave-approval",
                path: `/${companySlug}/admin-leave-approval`,
                title: "sidebar.admin-leave-management",
                submenu: false,
                permission: [config.RESOURCES.ADMIN_LEAVE_MANAGEMENT],
              }] : [],
              ...(getResource(config.RESOURCES.LEAVE_MANAGEMENT, ["read"])) ? [{
                key: "user-leave-approval",
                path: `/${companySlug}/user-leave-approve`,
                title: "sidebar.user-leave-management",
                submenu: false,
                permission: [config.RESOURCES.LEAVE_MANAGEMENT],
              }] : [],
              ...(getResource(config.RESOURCES.ADMIN_REGULARIZATION, ["read"])) ? [{
                key: "admin-regularizatrion",
                path: `/${companySlug}/admin-regularizatrion`,
                title: "sidebar.admin-regularizatrion",
                submenu: false,
                permission: [config.RESOURCES.ADMIN_REGULARIZATION],
              }] : [],
              ...(getResource(config.RESOURCES.TEAM_REGULARIZATION, ["read"])) ? [{
                key: "ManageTeamRegularization",
                path: `/${companySlug}/manage-team-regularization`,
                title: "sidebar.ManageTeamRegularization",
                submenu: false,
                permission: [config.RESOURCES.TEAM_REGULARIZATION],
              }] : [],
              // ...(getResource(config.RESOURCES.KPI, ["read"])) ? [{
              //   key: "Kpis",
              //   path: `/${companySlug}/Kpis`,
              //   title: "sidebar.manage-kpis",
              //   submenu: false,
              //   permission: [config.RESOURCES.KPI],
              // }] : [],
            ],
          },
        ]
        : []),
      // Performance Review submenu
      ...(performanceMenu.subMenuInfo.length > 0 ? [performanceMenu] : []),

      // Learning (LMS) submenu
      ...(hasAnyLmsPermission() ? [learningMenu] : []),

      // Enhanced Expense Management submenu
      ...(expenseMenu.subMenuInfo.length > 0 ? [expenseMenu] : []),
      ...(skillsMenu.subMenuInfo.length > 0 ? [skillsMenu] : []),
      ...(careerPathMenu.subMenuInfo.length > 0 ? [careerPathMenu] : []),

      // Onboarding submenu
      ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"]) ||
        getResource(config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST, ["read"]) ||
        getResource(config.RESOURCES.NEW_JOINEES, ["read"])
        ? [
          {
            icon: <OnboardingIcon />,
            className: " main-submenu",
            key: "sub19",
            title: "Onboarding",
            submenu: true,
            subMenuInfo: [
              ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"])) ? [{
                key: "company-doc-master",
                path: `/${companySlug}/company-doc-master`,
                title: "sidebar.docMaster",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_INITIATION],
              }] : [],
              ...(getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"])) ? [{
                key: "candidate-approvel",
                path: `/${companySlug}/candidate-approval`,
                title: "sidebar.candidate-approvel",
                submenu: false,
                permission: [config.RESOURCES.CANDIDATE_APPROVAL],
              }] : [],
              ...(getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"])) ? [{
                key: "candidate-list",
                path: `/${companySlug}/candidate-list`,
                title: "sidebar.candidate-approvel-list",
                submenu: false,
                permission: [config.RESOURCES.CANDIDATE_APPROVAL],
              }] : [],
              ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"])) ? [{
                key: "initiation",
                path: `/${companySlug}/onboarding-initiation`,
                title: "sidebar.onboarding-initiation",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_INITIATION],
              }] : [],
              ...(getResource(config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST, ["read"])) ? [{
                key: "doc-approve-list",
                path: `/${companySlug}/doc-approve-list`,
                title: "sidebar.doc-approve-list",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST],
              }] : [],
              ...(getResource(config.RESOURCES.NEW_JOINEES, ["read"])) ? [{
                key: "new-joinees-list",
                path: `/${companySlug}/new-joinees-list`,
                title: "sidebar.new-joinees-list",
                submenu: false,
                permission: [config.RESOURCES.NEW_JOINEES],
              }] : [],
            ],
          },
        ]
        : []),
      // Audit submenu
      // ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"]) ||
      //   getResource(config.RESOURCES.AUDITS, ["read"]) ||
      //   getResource(config.RESOURCES.AUDITS_PERFORMANCE, ["read"]) ||
      //   getResource(config.RESOURCES.AUDIT_HEAD, ["read"])
      //   ? [
      //     {
      //       icon: <AuditIcon />,
      //       className: "main-submenu",
      //       key: "sub20",
      //       title: "Audit",
      //       submenu: true,
      //       subMenuInfo: [
      //         ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"])) ? [{
      //           key: "Audit Master",
      //           path: `/${companySlug}/auditmaster`,
      //           title: "Audits Master",
      //           submenu: false,
      //           permission: [config.RESOURCES.AUDIT_MASTER],
      //         }] : [],
      //         ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"])) ? [{
      //           key: "Audits",
      //           path: `/${companySlug}/audits`,
      //           title: "Audits",
      //           submenu: false,
      //           permission: [config.RESOURCES.AUDIT_MASTER],
      //         }] : [],
      //         ...(getResource(config.RESOURCES.AUDITS_PERFORMANCE, ["read"])) ? [{
      //           key: "auditperformance",
      //           path: `/${companySlug}/auditperformance`,
      //           title: "Audit Performance",
      //           submenu: false,
      //           permission: [config.RESOURCES.AUDITS_PERFORMANCE],
      //         }] : [],
      //         ...(getResource(config.RESOURCES.AUDIT_HEAD, ["read"])) ? [{
      //           key: "auditHead",
      //           path: `/${companySlug}/auditHead`,
      //           title: "Audit Head",
      //           submenu: false,
      //           permission: [config.RESOURCES.AUDIT_HEAD],
      //         }] : [],
      //       ],
      //     },
      //   ]
      //   : []),
      // Organization Tree (note: key is "organisationtree" in SidebarContent but using "organization-tree" for consistency)
      {
        key: "organisationtree",
        path: `/${companySlug}/organization-view`,
        icon: <ImTree />,
        title: "sidebar.organisation-tree",
        submenu: false,
        permission: [],
      },
      // "More" submenu - always shown for Recruiter (but items are conditional)
      {
        icon: <i className="fas fa-ellipsis-h" />,
        className: " main-submenu",
        key: "sub18",
        title: "More",
        submenu: true,
        subMenuInfo: [
          ...(getResource(config.RESOURCES.JOB_OPENING, ["read"])) ? [{
            key: "Job Opening",
            path: `/${companySlug}/jobs/openings`,
            title: "sidebar.admin-jobopening",
            submenu: false,
            permission: [config.RESOURCES.JOB_OPENING],
          }] : [],
          // ...(getResource(config.RESOURCES.TEAM_GOAL, ["read"])) ? [{
          //   key: "team-goals",
          //   path: `/${companySlug}/team-goals`,
          //   title: "sidebar.team-goals",
          //   submenu: false,
          //   permission: [config.RESOURCES.TEAM_GOAL],
          // }] : [],
          ...(getResource(config.RESOURCES.INVENTORY, ["read"])) ? [{
            key: "inventory",
            path: `/${companySlug}/inventory`,
            title: "sidebar.user-assetstabpane",
            submenu: false,
            permission: [config.RESOURCES.INVENTORY],
          }] : [],
          ...(getResource(config.RESOURCES.EMPLOYEE_DETAILS, ["read"])) ? [{
            key: "view-employee-details",
            path: `/${companySlug}/viewEmployeeDetails`,
            title: "sidebar.employee-details",
            submenu: false,
            permission: [config.RESOURCES.EMPLOYEE_DETAILS],
          }] : [],
          ...(isPlanEnabled("organization_policy") ? [{
            key: "organization-policy",
            path: `/${companySlug}/organization-policy`,
            title: "sidebar.organization-policies",
            submenu: false,
            permission: [],
          }] : []),
        ],
      },
    ];
  };

  return useMemo(() => {
    // Return menu array based on role
    // Sub-Admin shares the Admin menu structure; every tab is still
    // permission-filtered by its consumers (SubmenuTabs).
    if (getRoles(["Admin"]) || getRoles(["Sub-Admin"])) {
      return buildAdminMenu();
    } else if (getRoles(["Intern"])) {
      // Intern has its own menu structure (all flat items, no submenus)
      return buildInternMenu();
    } else if (getRoles(["Recruiter"])) {
      // Recruiter has its own menu structure
      const menu = buildRecruiterMenu();

      // Add Recruiter-specific Management submenu
      const recruiterManagementItems = buildRecruiterManagementMenuItems();
      if (recruiterManagementItems.length > 0) {
        // Insert Management submenu before Onboarding or Audit or More
        const insertIndex = menu.findIndex(item =>
          (item.submenu && item.key === "sub19") || // Onboarding
          // (item.submenu && item.key === "sub20") || // Audit
          (item.submenu && item.key === "sub18") // More
        );
        if (insertIndex >= 0) {
          menu.splice(insertIndex, 0, ...recruiterManagementItems);
        } else {
          // Insert after Team submenu
          const teamIndex = menu.findIndex(item => item.submenu && item.key === "sub16");
          if (teamIndex >= 0) {
            menu.splice(teamIndex + 1, 0, ...recruiterManagementItems);
          } else {
            menu.push(...recruiterManagementItems);
          }
        }
      }

      return menu;
    } else if (getRoles(["User"]) || getRoles(["HR"]) || getRoles(["Account Team"]) ||
      getRoles(["Auditor"]) || getRoles(["Reporting Manager"]) ||
      getRoles(["Assistance Reporting Manager"]) || getRoles(["Network Team"])) {
      const menu = buildUserMenu();

      // Add HR-specific Management submenu
      if (getRoles(["HR"])) {
        const hrMenuItems = buildHRMenuItems();
        if (hrMenuItems.length > 0) {
          // Insert Management submenu before Onboarding or Audit or More
          const insertIndex = menu.findIndex(item =>
            (item.submenu && item.key === "sub19") || // Onboarding
            // (item.submenu && item.key === "sub20") || // Audit
            (item.submenu && item.key === "sub18") // More
          );
          if (insertIndex >= 0) {
            menu.splice(insertIndex, 0, ...hrMenuItems);
          } else {
            // Insert after Team submenu
            const teamIndex = menu.findIndex(item => item.submenu && item.key === "sub16");
            if (teamIndex >= 0) {
              menu.splice(teamIndex + 1, 0, ...hrMenuItems);
            } else {
              menu.push(...hrMenuItems);
            }
          }
        }
      }

      // Add HR-specific "Engage" submenu (Announcements moved out of "More")
      if (getRoles(["HR"])) {
        const engageMenu = buildEngageMenu();
        if (engageMenu) {
          const engageInsertIndex = menu.findIndex(item =>
            item.key === "Kpis" ||
            (item.submenu && item.key === "sub22") ||
            item.key === "organization-tree"
          );
          if (engageInsertIndex >= 0) {
            menu.splice(engageInsertIndex, 0, engageMenu);
          } else {
            menu.push(engageMenu);
          }
        }
      }

      // Add role-specific "More" menu
      const moreMenu = buildMoreMenu();
      if (moreMenu) {
        // For Auditor role: "More" menu should come AFTER organization-tree
        if (getRoles(["Auditor"])) {
          // Find organization-tree
          const orgTreeIndex = menu.findIndex(item => item.key === "organization-tree");
          if (orgTreeIndex >= 0) {
            // Insert after organization-tree
            menu.splice(orgTreeIndex + 1, 0, moreMenu);
          } else {
            // Fallback: add at the end
            menu.push(moreMenu);
          }
        } else {
          // For other roles: Insert "More" menu before KPI or Survey or organization-tree
          const insertIndex = menu.findIndex(item =>
            item.key === "Kpis" ||
            (item.submenu && item.key === "sub22") ||
            item.key === "organization-tree"
          );
          if (insertIndex >= 0) {
            menu.splice(insertIndex, 0, moreMenu);
          } else {
            // Insert before organization-tree or at the end
            const orgTreeIndex = menu.findIndex(item => item.key === "organization-tree");
            if (orgTreeIndex >= 0) {
              menu.splice(orgTreeIndex, 0, moreMenu);
            } else {
              menu.push(moreMenu);
            }
          }
        }
      }
      return menu;
    }

    return [];
  }, [companySlug, authUser]);
};
